import React, { useEffect, useMemo, useState } from 'react';
import TotpQrCode from '../components/TotpQrCode';
import {
  agruparVisitasPorActividad,
  resumirDistribucionActividades,
} from '../utils/dashboardMetrics';
import {
  API_BASE,
  confirmTotp,
  formatApiError,
  getStoredUser,
  getToken,
  isAuthError,
  notifySessionExpired,
  setSession,
  setupTotp,
} from '../services/api';

const columnas = [
  'num_gestion',
  'cod_cliente',
  'r_social',
  'direccion',
  'comuna',
  'medidor',
  'm_medidor',
  'deuda_vencida',
];

const tabs = [
  { id: 'indicadores', label: 'Indicadores' },
  { id: 'carga', label: 'Carga de BD' },
  { id: 'asignacion', label: 'Asignación' },
  { id: 'respaldos', label: 'Respaldos PDF' },
  { id: 'usuarios', label: 'Gestión de usuarios' },
  { id: 'configuracion', label: 'Configuración' },
];

const roleValues = (rol) => {
  if (rol === 'admin_tecnico') return ['admin', 'tecnico'];
  return String(rol || '').toLowerCase().split(/[,+|]/).map((item) => item.trim()).filter(Boolean);
};
const hasRole = (rol, role) => (
  (rol === 'superadmin' && ['admin', 'tecnico'].includes(role)) || roleValues(rol).includes(role)
);
const isSuperadmin = (rol) => rol === 'superadmin';
const isAdminRole = (rol) => hasRole(rol, 'admin');
const isTecnicoRole = (rol) => hasRole(rol, 'tecnico');
const isTecnicoAsignable = (usuario) => (
  usuario?.activo && isTecnicoRole(usuario?.rol)
);
const roleLabel = (rol) => ({
  admin: 'Administrador',
  tecnico: 'Tecnico de Terreno',
  admin_tecnico: 'Administrador y Tecnico',
  superadmin: 'Super Administrador y Tecnico',
}[rol] || rol);
const clienteEstadoLabel = (estado) => ({
  pendiente: 'Pendiente',
  asignado: 'Asignado',
  reprogramado: 'Reprogramado',
  revision_admin: 'Revision admin',
  finalizado: 'Finalizado',
}[estado] || estado || 'Pendiente');
const tieneDecisionAdminPendiente = (cliente) => (
  cliente?.estado !== 'finalizado' &&
  (
    cliente?.estado === 'revision_admin' ||
    (
      cliente?.ultima_visita &&
      cliente.ultima_visita.resultado_visita &&
      cliente.ultima_visita.resultado_visita !== 'finalizada'
    )
  )
);
const RESPALDOS_REFRESH_MS = 15000;
const INDICADORES_REFRESH_MS = 30000;
const VISITAS_POR_PAGINA = 20;

const fechaValida = (valor) => {
  if (!valor) return null;
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

const claveDiaLocal = (fecha) => (
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`
);

const claveMesLocal = (fecha) => (
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`
);

const inicioMesDesdeClave = (clave) => {
  const [anio, mes] = String(clave || '').split('-').map(Number);
  if (!anio || !mes) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  return new Date(anio, mes - 1, 1);
};

const mismoMes = (fecha, base) => (
  fecha &&
  fecha.getFullYear() === base.getFullYear() &&
  fecha.getMonth() === base.getMonth()
);

const esCierreEfectivo = (visita) => (
  (visita?.resultado_visita || visita?.estado) === 'finalizada'
);

const compararValor = (actual, previo) => {
  const diferencia = actual - previo;
  const porcentaje = previo ? Math.round((diferencia / previo) * 100) : null;
  const tono = diferencia > 0 ? 'up' : diferencia < 0 ? 'down' : 'flat';
  return { diferencia, porcentaje, tono };
};

const capitalizarPrimeraLetra = (valor) => (
  valor ? `${valor.charAt(0).toUpperCase()}${valor.slice(1)}` : valor
);

const formatearMesTitulo = (fecha, opciones = { month: 'long', year: 'numeric' }) => (
  capitalizarPrimeraLetra(fecha.toLocaleDateString('es-CL', opciones))
);

const DashboardAdmin = () => {
  const currentUser = getStoredUser();
  const canManageConfig = isAdminRole(currentUser?.rol);
  const canGrantAdmin = isSuperadmin(currentUser?.rol);

  const [activeTab, setActiveTab] = useState('indicadores');
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newRole, setNewRole] = useState('tecnico');
  const [temporaryPassword, setTemporaryPassword] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState('');
  const [resetMfaUserId, setResetMfaUserId] = useState('');
  const [visitasAdmin, setVisitasAdmin] = useState([]);
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [visitasError, setVisitasError] = useState('');
  const [pdfZipDesde, setPdfZipDesde] = useState('');
  const [pdfZipHasta, setPdfZipHasta] = useState('');
  const [pdfZipMes, setPdfZipMes] = useState('');
  const [pdfZipLoading, setPdfZipLoading] = useState(false);
  const [pdfZipStatus, setPdfZipStatus] = useState('');
  const [informeMetricasLoading, setInformeMetricasLoading] = useState(false);
  const [informeMetricasStatus, setInformeMetricasStatus] = useState('');
  const [filtroComunaRespaldos, setFiltroComunaRespaldos] = useState('');
  const [filtroMesRespaldos, setFiltroMesRespaldos] = useState('');
  const [filtroRevisionRespaldos, setFiltroRevisionRespaldos] = useState('');
  const [busquedaRespaldos, setBusquedaRespaldos] = useState('');
  const [clientesAdmin, setClientesAdmin] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [clientesError, setClientesError] = useState('');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [filtroAsignacion, setFiltroAsignacion] = useState('pendientes');
  const [filtroComunaAsignacion, setFiltroComunaAsignacion] = useState('');
  const [assigningKey, setAssigningKey] = useState('');
  const [priorityKey, setPriorityKey] = useState('');
  const [closingKey, setClosingKey] = useState('');
  const [bulkComuna, setBulkComuna] = useState('');
  const [bulkTecnicoId, setBulkTecnicoId] = useState('');
  const [bulkSoloSinAsignar, setBulkSoloSinAsignar] = useState(true);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');
  const [periodoIndicadores, setPeriodoIndicadores] = useState('dia');
  const [filtroMesIndicadores, setFiltroMesIndicadores] = useState(() => claveMesLocal(new Date()));
  const [vistaProductividad, setVistaProductividad] = useState('semanal');
  const [filtroComunaIndicadores, setFiltroComunaIndicadores] = useState('');
  const [busquedaIndicadores, setBusquedaIndicadores] = useState('');
  const [paginaIndicadores, setPaginaIndicadores] = useState(1);
  const [mostrarTodasActividades, setMostrarTodasActividades] = useState(false);
  const [tiposActividad, setTiposActividad] = useState([]);
  const [actividadNueva, setActividadNueva] = useState('');
  const [actividadStatus, setActividadStatus] = useState('');
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStatus, setMfaStatus] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(Boolean(currentUser?.totp_enabled));

  const availableRoles = canGrantAdmin ? ['admin', 'tecnico', 'admin_tecnico'] : ['tecnico'];
  const editableUsers = usuarios.filter((usuario) => !isSuperadmin(usuario.rol));
  const tecnicosAsignables = usuarios.filter(isTecnicoAsignable);
  const tieneTecnicoValido = (cliente) => Boolean(
    cliente.tecnico_asignado && isTecnicoAsignable(cliente.tecnico)
  );
  const tieneAsignacionNoTecnica = (cliente) => Boolean(
    cliente.tecnico_asignado && !tieneTecnicoValido(cliente)
  );
  const selectedUser = useMemo(
    () => editableUsers.find((usuario) => usuario.id === Number(selectedUserId)),
    [editableUsers, selectedUserId]
  );
  const comunasAsignacion = useMemo(() => (
    [...new Set(clientesAdmin.map((cliente) => cliente.comuna).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [clientesAdmin]);
  const bulkAsignacionPreview = useMemo(() => {
    if (!bulkComuna) return { total: 0, sinTecnico: 0, asignados: 0 };
    const casosComuna = clientesAdmin.filter((cliente) => (
      cliente.comuna === bulkComuna && cliente.estado !== 'finalizado'
    ));
    const sinTecnico = casosComuna.filter((cliente) => !tieneTecnicoValido(cliente)).length;
    const asignados = casosComuna.filter((cliente) => tieneTecnicoValido(cliente)).length;
    return {
      total: bulkSoloSinAsignar ? sinTecnico : casosComuna.length,
      sinTecnico,
      asignados,
    };
  }, [clientesAdmin, bulkComuna, bulkSoloSinAsignar]);

  const readApiJson = async (res, fallback) => {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      if (isAuthError(res, data)) notifySessionExpired();
      throw new Error(formatApiError(res, data, fallback));
    }
    return data;
  };

  const cargarUsuarios = async () => {
    try {
      const res = await fetch(`${API_BASE}/usuarios/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudieron cargar los usuarios.');
      setUsuarios(data);
      const primerEditable = data.find((usuario) => !isSuperadmin(usuario.rol));
      setSelectedUserId((actual) => actual || primerEditable?.id || '');
    } catch (err) {
      alert(err.message);
    }
  };

  const indicadores = useMemo(() => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setHours(0, 0, 0, 0);
    let fin = new Date(inicio);
    let periodoLabel = capitalizarPrimeraLetra(
      inicio.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })
    );

    if (periodoIndicadores === 'semana') {
      const diaSemana = inicio.getDay() || 7;
      inicio.setDate(inicio.getDate() - diaSemana + 1);
      fin = new Date(inicio);
      fin.setDate(fin.getDate() + 7);
      const ultimoDia = new Date(fin);
      ultimoDia.setDate(ultimoDia.getDate() - 1);
      periodoLabel = `${inicio.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })} – ${ultimoDia.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (periodoIndicadores === 'mes') {
      const mesSeleccionado = inicioMesDesdeClave(filtroMesIndicadores);
      inicio.setTime(mesSeleccionado.getTime());
      fin = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 1);
      periodoLabel = formatearMesTitulo(inicio);
    } else {
      fin.setDate(fin.getDate() + 1);
    }

    const visitasPeriodo = visitasAdmin.filter((visita) => {
      const fechaBase = visita.fech_termino || visita.fecha_ini;
      if (!fechaBase) return false;
      const fecha = new Date(fechaBase);
      return fecha >= inicio && fecha < fin;
    }).sort((a, b) => {
      const fechaA = new Date(a.fech_termino || a.fecha_ini || 0).getTime();
      const fechaB = new Date(b.fech_termino || b.fecha_ini || 0).getTime();
      return fechaB - fechaA;
    });

    const total = visitasPeriodo.length;
    const conPdf = visitasPeriodo.filter((visita) => visita.pdf_disponible).length;
    const conFotos = visitasPeriodo.filter((visita) => (visita.fotos?.length || 0) > 0).length;
    const fotosTotales = visitasPeriodo.reduce((acc, visita) => acc + (visita.fotos?.length || 0), 0);

    const pendientesAsignados = clientesAdmin.filter((cliente) => (
      cliente.estado !== 'finalizado' && tieneTecnicoValido(cliente)
    ));

    const porTecnicoMap = visitasPeriodo.reduce((acc, visita) => {
      const id = visita.tecnico?.id || visita.tecnico?.nombre || 'sin-tecnico';
      const nombre = visita.tecnico?.nombre || 'Sin técnico';
      if (!acc[id]) acc[id] = { id, nombre, total: 0, pdf: 0, fotos: 0, pendientes: 0 };
      acc[id].total += 1;
      acc[id].pdf += visita.pdf_disponible ? 1 : 0;
      acc[id].fotos += visita.fotos?.length || 0;
      return acc;
    }, {});

    pendientesAsignados.forEach((cliente) => {
      const id = cliente.tecnico?.id || cliente.tecnico_asignado || cliente.tecnico?.nombre || 'sin-tecnico';
      const nombre = cliente.tecnico?.nombre || 'Sin técnico';
      if (!porTecnicoMap[id]) porTecnicoMap[id] = { id, nombre, total: 0, pdf: 0, fotos: 0, pendientes: 0 };
      porTecnicoMap[id].pendientes += 1;
    });

    const porTecnico = Object.values(porTecnicoMap)
      .sort((a, b) => (b.pendientes - a.pendientes) || (b.total - a.total));

    const porActividad = agruparVisitasPorActividad(visitasPeriodo);
    const resumenActividad = resumirDistribucionActividades(porActividad);

    const recientes = [...visitasPeriodo].slice(0, 5);

    return {
      total,
      totalGeneral: visitasAdmin.length,
      conPdf,
      conFotos,
      fotosTotales,
      pendientesAsignados: pendientesAsignados.length,
      porTecnico,
      porActividad,
      porActividadResumen: resumenActividad.actividades,
      tiposActividadAgrupados: resumenActividad.tiposAgrupados,
      actividadPrincipal: porActividad[0] || null,
      recientes,
      visitasPeriodo,
      coberturaPdf: total ? Math.round((conPdf / total) * 100) : 0,
      coberturaFotos: total ? Math.round((conFotos / total) * 100) : 0,
      desde: inicio,
      hasta: fin,
      periodoLabel,
    };
  }, [visitasAdmin, clientesAdmin, periodoIndicadores, filtroMesIndicadores]);

  const mesesIndicadores = useMemo(() => {
    const meses = visitasAdmin.reduce((acc, visita) => {
      const fechaBase = visita.fech_termino || visita.fecha_ini || visita.pdf_visto_at;
      const fecha = fechaValida(fechaBase);
      if (!fecha) return acc;
      const clave = claveMesLocal(fecha);
      acc.set(clave, formatearMesTitulo(fecha));
      return acc;
    }, new Map([[claveMesLocal(new Date()), formatearMesTitulo(new Date())]]));

    return [...meses.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([value, label]) => ({ value, label }));
  }, [visitasAdmin]);

  const indicadoresV21 = useMemo(() => {
    const inicioMes = inicioMesDesdeClave(filtroMesIndicadores);
    const inicioMesAnterior = new Date(inicioMes.getFullYear(), inicioMes.getMonth() - 1, 1);
    const diasDelMes = new Date(inicioMes.getFullYear(), inicioMes.getMonth() + 1, 0).getDate();
    const clientesActivos = clientesAdmin.filter((cliente) => cliente.estado !== 'finalizado');
    const asignadosActuales = clientesActivos.filter((cliente) => tieneTecnicoValido(cliente));
    const sinAsignarActuales = clientesActivos.filter((cliente) => !tieneTecnicoValido(cliente));
    const pendientesAsignados = clientesActivos.filter((cliente) => tieneTecnicoValido(cliente));
    const visitasMes = visitasAdmin.filter((visita) => mismoMes(fechaValida(visita.fech_termino), inicioMes));
    const visitasMesAnterior = visitasAdmin.filter((visita) => mismoMes(fechaValida(visita.fech_termino), inicioMesAnterior));
    const cierresMes = visitasMes.filter(esCierreEfectivo);
    const cierresMesAnterior = visitasMesAnterior.filter(esCierreEfectivo);
    const intentosMes = visitasMes.filter((visita) => !esCierreEfectivo(visita));
    const pdfMes = visitasMes.filter((visita) => visita.pdf_disponible).length;
    const revisadosMes = visitasMes.filter((visita) => visita.pdf_visto_at).length;
    const pdfMesAnterior = visitasMesAnterior.filter((visita) => visita.pdf_disponible).length;
    const revisadosMesAnterior = visitasMesAnterior.filter((visita) => visita.pdf_visto_at).length;
    const respaldosSinRevisar = visitasAdmin.filter((visita) => visita.pdf_disponible && !visita.pdf_visto_at).length;
    const descargadosSinLeer = visitasAdmin.filter((visita) => visita.pdf_descargado_at && !visita.pdf_visto_at).length;
    const cerradasSinPdf = visitasMes.filter((visita) => !visita.pdf_disponible).length;
    const promedioDiarioMes = diasDelMes ? Math.round((visitasMes.length / diasDelMes) * 10) / 10 : 0;
    const productividadDiaria = Array.from({ length: diasDelMes }, (_, index) => {
      const fechaDia = new Date(inicioMes.getFullYear(), inicioMes.getMonth(), index + 1);
      const clave = claveDiaLocal(fechaDia);
      const acciones = visitasAdmin.filter((visita) => {
        const fechaCierre = fechaValida(visita.fech_termino);
        return fechaCierre && claveDiaLocal(fechaCierre) === clave;
      }).length;
      const cerradas = visitasAdmin.filter((visita) => {
        const fechaCierre = fechaValida(visita.fech_termino);
        return fechaCierre && claveDiaLocal(fechaCierre) === clave && esCierreEfectivo(visita);
      }).length;
      const pdf = visitasAdmin.filter((visita) => {
        const fechaCierre = fechaValida(visita.fech_termino);
        return fechaCierre && claveDiaLocal(fechaCierre) === clave && visita.pdf_disponible;
      }).length;
      const revisados = visitasAdmin.filter((visita) => {
        const fechaRevision = fechaValida(visita.pdf_visto_at);
        return fechaRevision && claveDiaLocal(fechaRevision) === clave;
      }).length;
      return { dia: index + 1, clave, acciones, cerradas, pdf, revisados };
    });
    const maxProductividadDia = Math.max(
      1,
      ...productividadDiaria.map((dia) => Math.max(dia.acciones, dia.cerradas, dia.pdf, dia.revisados))
    );
    const embudoBase = [
      { id: 'carga', label: 'Carga actual', value: clientesAdmin.length },
      { id: 'asignados', label: 'Asignados', value: asignadosActuales.length },
      { id: 'acciones', label: 'Acciones mes', value: visitasMes.length },
      { id: 'cerrados', label: 'Cierres efectivos', value: cierresMes.length },
      { id: 'pdf', label: 'Con PDF', value: pdfMes },
      { id: 'revisados', label: 'Revisados', value: revisadosMes },
    ];
    const embudoMensual = embudoBase.map((item, index) => {
      const anterior = embudoBase[index - 1];
      const pctAnterior = anterior?.value ? Math.round((item.value / anterior.value) * 100) : null;
      return {
        ...item,
        pctAnterior,
        referencia: index === 0
          ? 'Base actual'
          : `${pctAnterior ?? 0}% de ${anterior.label.toLowerCase()}`,
      };
    });
    const comparativas = [
      { id: 'acciones', label: 'Acciones', actual: visitasMes.length, previo: visitasMesAnterior.length },
      { id: 'cerradas', label: 'Cierres', actual: cierresMes.length, previo: cierresMesAnterior.length },
      { id: 'pdf', label: 'PDF', actual: pdfMes, previo: pdfMesAnterior },
      { id: 'revisados', label: 'Revisados', actual: revisadosMes, previo: revisadosMesAnterior },
    ].map((item) => ({
      ...item,
      ...compararValor(item.actual, item.previo),
    }));
    const maxEmbudo = Math.max(1, ...embudoMensual.map((item) => item.value));
    const tendenciaMap = visitasAdmin.reduce((acc, visita) => {
      const fechaCierre = fechaValida(visita.fech_termino);
      const fechaRevision = fechaValida(visita.pdf_visto_at);
      const fechaBase = fechaCierre || fechaRevision;
      if (!fechaBase) return acc;
      const clave = claveMesLocal(fechaBase);
      if (!acc[clave]) {
        acc[clave] = {
          clave,
          fecha: new Date(fechaBase.getFullYear(), fechaBase.getMonth(), 1),
          label: formatearMesTitulo(fechaBase, { month: 'short', year: 'numeric' }),
          acciones: 0,
          cerradas: 0,
          pdf: 0,
          revisados: 0,
        };
      }
      if (fechaCierre && claveMesLocal(fechaCierre) === clave) {
        acc[clave].acciones += 1;
        acc[clave].cerradas += esCierreEfectivo(visita) ? 1 : 0;
        acc[clave].pdf += visita.pdf_disponible ? 1 : 0;
      }
      if (fechaRevision && claveMesLocal(fechaRevision) === clave) {
        acc[clave].revisados += 1;
      }
      return acc;
    }, {});
    const tendenciaMensual = Object.values(tendenciaMap)
      .sort((a, b) => a.fecha - b.fecha)
      .slice(-6);
    const maxTendenciaMensual = Math.max(
      1,
      ...tendenciaMensual.map((mes) => Math.max(mes.acciones, mes.cerradas, mes.pdf, mes.revisados))
    );
    const tecnicosMap = {};

    visitasMes.forEach((visita) => {
      const id = visita.tecnico?.id || visita.tecnico?.nombre || 'sin-tecnico';
      const nombre = visita.tecnico?.nombre || 'Sin tecnico';
      if (!tecnicosMap[id]) {
        tecnicosMap[id] = { id, nombre, acciones: 0, cerradas: 0, intentos: 0, pendientes: 0, pdf: 0, revisados: 0 };
      }
      tecnicosMap[id].acciones += 1;
      tecnicosMap[id].cerradas += esCierreEfectivo(visita) ? 1 : 0;
      tecnicosMap[id].intentos += esCierreEfectivo(visita) ? 0 : 1;
      tecnicosMap[id].pdf += visita.pdf_disponible ? 1 : 0;
      tecnicosMap[id].revisados += visita.pdf_visto_at ? 1 : 0;
    });

    pendientesAsignados.forEach((cliente) => {
      const id = cliente.tecnico?.id || cliente.tecnico_asignado || cliente.tecnico?.nombre || 'sin-tecnico';
      const nombre = cliente.tecnico?.nombre || 'Sin tecnico';
      if (!tecnicosMap[id]) {
        tecnicosMap[id] = { id, nombre, acciones: 0, cerradas: 0, intentos: 0, pendientes: 0, pdf: 0, revisados: 0 };
      }
      tecnicosMap[id].pendientes += 1;
    });

    const todosTecnicosOperacion = Object.values(tecnicosMap)
      .map((tecnico) => {
        const coberturaRevision = tecnico.pdf ? Math.round((tecnico.revisados / tecnico.pdf) * 100) : 0;
        const presionOperativa = Math.round((tecnico.pendientes / Math.max(tecnico.acciones, 1)) * 10) / 10;
        const riesgo = tecnico.pendientes >= 15 || (tecnico.pdf >= 5 && coberturaRevision < 50)
          ? 'critical'
          : tecnico.pendientes >= 8 || (tecnico.pdf > 0 && coberturaRevision < 80)
            ? 'warn'
            : 'ok';
        const riesgoLabel = riesgo === 'critical' ? 'Alta carga' : riesgo === 'warn' ? 'Atencion' : 'Normal';
        const accion = riesgo === 'critical'
          ? 'Redistribuir o priorizar cierres.'
          : riesgo === 'warn'
            ? 'Monitorear pendientes y revision PDF.'
            : 'Sin accion urgente.';
        return {
          ...tecnico,
          coberturaRevision,
          presionOperativa,
          riesgo,
          riesgoLabel,
          accion,
        };
      })
      .sort((a, b) => (b.pendientes - a.pendientes) || (b.acciones - a.acciones));
    const resumenTecnicos = {
      total: todosTecnicosOperacion.length,
      pendientes: todosTecnicosOperacion.reduce((total, tecnico) => total + tecnico.pendientes, 0),
      acciones: todosTecnicosOperacion.reduce((total, tecnico) => total + tecnico.acciones, 0),
      cerradas: todosTecnicosOperacion.reduce((total, tecnico) => total + tecnico.cerradas, 0),
      intentos: todosTecnicosOperacion.reduce((total, tecnico) => total + tecnico.intentos, 0),
      atencion: todosTecnicosOperacion.filter((tecnico) => tecnico.riesgo !== 'ok').length,
    };
    const tecnicosOperacion = todosTecnicosOperacion.slice(0, 6);
    const maxTecnicoCarga = Math.max(
      1,
      ...tecnicosOperacion.map((tecnico) => Math.max(tecnico.pendientes, tecnico.acciones, tecnico.cerradas))
    );

    const comunasMap = {};
    clientesActivos.forEach((cliente) => {
      const comuna = cliente.comuna || 'Sin comuna';
      if (!comunasMap[comuna]) {
        comunasMap[comuna] = { comuna, activos: 0, asignados: 0, sinAsignar: 0, cerradas: 0 };
      }
      comunasMap[comuna].activos += 1;
      if (tieneTecnicoValido(cliente)) {
        comunasMap[comuna].asignados += 1;
      } else {
        comunasMap[comuna].sinAsignar += 1;
      }
    });

    visitasMes.forEach((visita) => {
      const comuna = visita.cliente?.comuna || 'Sin comuna';
      if (!comunasMap[comuna]) {
        comunasMap[comuna] = { comuna, activos: 0, asignados: 0, sinAsignar: 0, cerradas: 0 };
      }
      comunasMap[comuna].cerradas += esCierreEfectivo(visita) ? 1 : 0;
    });

    const todasComunasOperacion = Object.values(comunasMap)
      .map((comuna) => {
        const coberturaAsignacion = comuna.activos ? Math.round((comuna.asignados / comuna.activos) * 100) : 0;
        const riesgo = comuna.sinAsignar >= 10
          ? 'critical'
          : comuna.sinAsignar > 0
            ? 'warn'
            : 'ok';
        const riesgoLabel = riesgo === 'critical' ? 'Alta exposicion' : riesgo === 'warn' ? 'Pendiente' : 'Cubierta';
        const accion = riesgo === 'critical'
          ? 'Asignar comuna antes de nueva carga.'
          : riesgo === 'warn'
            ? 'Completar asignacion pendiente.'
            : 'Mantener seguimiento.';
        return {
          ...comuna,
          coberturaAsignacion,
          riesgo,
          riesgoLabel,
          accion,
        };
      })
      .sort((a, b) => (b.sinAsignar - a.sinAsignar) || (b.activos - a.activos) || (b.cerradas - a.cerradas));
    const resumenComunas = {
      total: todasComunasOperacion.length,
      activos: todasComunasOperacion.reduce((total, comuna) => total + comuna.activos, 0),
      sinAsignar: todasComunasOperacion.reduce((total, comuna) => total + comuna.sinAsignar, 0),
      coberturaAsignacion: asignadosActuales.length || clientesActivos.length
        ? Math.round((asignadosActuales.length / Math.max(clientesActivos.length, 1)) * 100)
        : 0,
    };
    const porComuna = todasComunasOperacion.slice(0, 6);
    const maxComunaCarga = Math.max(
      1,
      ...porComuna.map((comuna) => Math.max(comuna.activos, comuna.cerradas))
    );
    const alertasOperativas = [
      sinAsignarActuales.length > 0 && {
        tipo: sinAsignarActuales.length > 10 ? 'critical' : 'warn',
        prioridad: sinAsignarActuales.length > 10 ? 1 : 3,
        prioridadLabel: sinAsignarActuales.length > 10 ? 'Alta' : 'Media',
        titulo: 'Casos sin asignar',
        detalle: `${sinAsignarActuales.length} caso(s) activos aun no tienen tecnico valido.`,
        accion: 'Asignar tecnico o revisar carga importada.',
        targetTab: 'asignacion',
      },
      pendientesAsignados.length > 0 && {
        tipo: pendientesAsignados.length > 20 ? 'critical' : 'warn',
        prioridad: pendientesAsignados.length > 20 ? 1 : 2,
        prioridadLabel: pendientesAsignados.length > 20 ? 'Alta' : 'Media',
        titulo: 'Pendientes de cierre',
        detalle: `${pendientesAsignados.length} caso(s) asignados siguen abiertos.`,
        accion: 'Revisar carga por tecnico y coordinar cierres.',
        targetId: 'dashboard-carga-tecnico',
      },
      respaldosSinRevisar > 0 && {
        tipo: respaldosSinRevisar > 20 ? 'critical' : 'warn',
        prioridad: respaldosSinRevisar > 20 ? 1 : 2,
        prioridadLabel: respaldosSinRevisar > 20 ? 'Alta' : 'Media',
        titulo: 'Respaldos sin revisar',
        detalle: `${respaldosSinRevisar} PDF disponible(s) aun no estan marcados como leidos.`,
        accion: 'Entrar a Respaldos PDF y marcar revision.',
        targetTab: 'respaldos',
      },
      cerradasSinPdf > 0 && {
        tipo: 'warn',
        prioridad: 2,
        prioridadLabel: 'Media',
        titulo: 'Cierres sin PDF',
        detalle: `${cerradasSinPdf} accion(es) tecnica(s) del mes no muestran PDF disponible.`,
        accion: 'Validar generacion de PDF o evidencia del cierre.',
        targetTab: 'respaldos',
      },
      descargadosSinLeer > 0 && {
        tipo: 'info',
        prioridad: 4,
        prioridadLabel: 'Baja',
        titulo: 'Descargados no leidos',
        detalle: `${descargadosSinLeer} respaldo(s) fueron descargados pero siguen sin lectura.`,
        accion: 'Confirmar si descarga equivale a revision real.',
        targetTab: 'respaldos',
      },
      cierresMes.length === 0 && asignadosActuales.length > 0 && {
        tipo: 'info',
        prioridad: 3,
        prioridadLabel: 'Media',
        titulo: 'Mes sin cierres',
        detalle: `No hay cierres efectivos registrados en ${formatearMesTitulo(inicioMes, { month: 'long' })}. Hay ${visitasMes.length} accion(es) tecnica(s).`,
        accion: 'Verificar si corresponde al inicio de mes o hay atraso.',
        targetId: 'dashboard-detalle-periodo',
      },
    ].filter(Boolean).sort((a, b) => a.prioridad - b.prioridad);

    return {
      clientesActivos: clientesActivos.length,
      asignadosActuales: asignadosActuales.length,
      sinAsignarActuales: sinAsignarActuales.length,
      pendientesAsignados: pendientesAsignados.length,
      visitasMes: visitasMes.length,
      visitasMesAnterior: visitasMesAnterior.length,
      cierresMes: cierresMes.length,
      cierresMesAnterior: cierresMesAnterior.length,
      intentosMes: intentosMes.length,
      pdfMes,
      revisadosMes,
      pdfMesAnterior,
      revisadosMesAnterior,
      respaldosSinRevisar,
      descargadosSinLeer,
      cerradasSinPdf,
      comparativas,
      productividadDiaria,
      maxProductividadDia,
      tendenciaMensual,
      maxTendenciaMensual,
      promedioDiarioMes,
      embudoMensual,
      maxEmbudo,
      tecnicosOperacion,
      resumenTecnicos,
      maxTecnicoCarga,
      porComuna,
      resumenComunas,
      maxComunaCarga,
      alertasOperativas,
      mesActualLabel: formatearMesTitulo(inicioMes),
      mesAnteriorLabel: formatearMesTitulo(inicioMesAnterior),
    };
  }, [visitasAdmin, clientesAdmin, filtroMesIndicadores]);

  const productividadGrafico = useMemo(() => {
    if (vistaProductividad === 'diaria') {
      return {
        datos: indicadoresV21.productividadDiaria.map((dia) => ({
          ...dia,
          etiqueta: String(dia.dia),
          descripcion: `Día ${dia.dia}`,
        })),
        maximo: indicadoresV21.maxProductividadDia,
      };
    }

    const semanas = indicadoresV21.productividadDiaria.reduce((acc, dia, index) => {
      const numeroSemana = Math.floor(index / 7) + 1;
      if (!acc[numeroSemana]) {
        acc[numeroSemana] = {
          clave: `semana-${numeroSemana}`,
          etiqueta: `S${numeroSemana}`,
          descripcion: `Semana ${numeroSemana}`,
          acciones: 0,
          cerradas: 0,
          pdf: 0,
          revisados: 0,
        };
      }
      acc[numeroSemana].acciones += dia.acciones;
      acc[numeroSemana].cerradas += dia.cerradas;
      acc[numeroSemana].pdf += dia.pdf;
      acc[numeroSemana].revisados += dia.revisados;
      return acc;
    }, {});
    const datos = Object.values(semanas);
    return {
      datos,
      maximo: Math.max(1, ...datos.map((semana) => Math.max(
        semana.acciones,
        semana.cerradas,
        semana.pdf,
        semana.revisados
      ))),
    };
  }, [indicadoresV21.productividadDiaria, indicadoresV21.maxProductividadDia, vistaProductividad]);

  const comunasIndicadores = useMemo(() => (
    [...new Set(indicadores.visitasPeriodo.map((visita) => visita.cliente?.comuna).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [indicadores.visitasPeriodo]);

  const comunasRespaldos = useMemo(() => (
    [...new Set(visitasAdmin.map((visita) => visita.cliente?.comuna).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [visitasAdmin]);

  const mesesRespaldos = useMemo(() => {
    const meses = visitasAdmin.reduce((acc, visita) => {
      const fechaBase = visita.fech_termino || visita.fecha_ini;
      if (!fechaBase) return acc;
      const fecha = new Date(fechaBase);
      if (Number.isNaN(fecha.getTime())) return acc;
      const valor = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      acc.set(valor, formatearMesTitulo(fecha));
      return acc;
    }, new Map());

    return [...meses.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([value, label]) => ({ value, label }));
  }, [visitasAdmin]);

  const aplicarMesZip = (valor) => {
    setPdfZipMes(valor);
    if (!valor) return;

    const inicio = inicioMesDesdeClave(valor);
    const fin = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 0);
    setPdfZipDesde(claveDiaLocal(inicio));
    setPdfZipHasta(claveDiaLocal(fin));
  };

  const formatearFechaHora = (valor) => {
    if (!valor) return 'No informada';
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return 'No informada';
    return fecha.toLocaleString('es-CL', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const visitasRespaldosFiltradas = useMemo(() => {
    const texto = busquedaRespaldos.trim().toLowerCase();
    return visitasAdmin.filter((visita) => {
      const cliente = visita.cliente || {};
      const fechaBase = visita.fech_termino || visita.fecha_ini;
      const fecha = fechaBase ? new Date(fechaBase) : null;
      const mesVisita = fecha && !Number.isNaN(fecha.getTime())
        ? `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`
        : '';
      const coincideComuna = !filtroComunaRespaldos || cliente.comuna === filtroComunaRespaldos;
      const coincideMes = !filtroMesRespaldos || mesVisita === filtroMesRespaldos;
      const coincideRevision =
        !filtroRevisionRespaldos ||
        (filtroRevisionRespaldos === 'pendientes' && visita.pdf_disponible && !visita.pdf_visto_at) ||
        (filtroRevisionRespaldos === 'nuevos' && !visita.pdf_visto_at && !visita.pdf_descargado_at) ||
        (filtroRevisionRespaldos === 'vistos' && Boolean(visita.pdf_visto_at)) ||
        (filtroRevisionRespaldos === 'descargados' && Boolean(visita.pdf_descargado_at)) ||
        (filtroRevisionRespaldos === 'no_descargados' && !visita.pdf_descargado_at) ||
        (filtroRevisionRespaldos === 'sin_pdf' && !visita.pdf_disponible);
      const coincideTexto = !texto || [
        cliente.cod_cliente,
        cliente.num_gestion,
        cliente.r_social,
        cliente.direccion,
        cliente.comuna,
        visita.tecnico?.nombre,
        visita.tipo_actividad,
        visita.nom_client,
        visita.obs_visita,
        formatearFechaHora(fechaBase),
      ].some((valor) => String(valor || '').toLowerCase().includes(texto));

      return coincideComuna && coincideMes && coincideRevision && coincideTexto;
    }).sort((a, b) => {
      const prioridadA = a.pdf_disponible && !a.pdf_visto_at ? 0 : a.pdf_disponible ? 1 : 2;
      const prioridadB = b.pdf_disponible && !b.pdf_visto_at ? 0 : b.pdf_disponible ? 1 : 2;
      if (prioridadA !== prioridadB) return prioridadA - prioridadB;
      const fechaA = new Date(a.fech_termino || a.fecha_ini || 0).getTime();
      const fechaB = new Date(b.fech_termino || b.fecha_ini || 0).getTime();
      return fechaB - fechaA;
    });
  }, [visitasAdmin, filtroComunaRespaldos, filtroMesRespaldos, filtroRevisionRespaldos, busquedaRespaldos]);

  const resumenRespaldos = useMemo(() => {
    const conPdf = visitasAdmin.filter((visita) => visita.pdf_disponible);
    const pendientes = conPdf.filter((visita) => !visita.pdf_visto_at);
    const nuevos = conPdf.filter((visita) => !visita.pdf_visto_at && !visita.pdf_descargado_at);
    const descargados = conPdf.filter((visita) => visita.pdf_descargado_at);
    const leidos = conPdf.filter((visita) => visita.pdf_visto_at);
    const sinPdf = visitasAdmin.filter((visita) => !visita.pdf_disponible);
    return {
      total: visitasAdmin.length,
      conPdf: conPdf.length,
      pendientes: pendientes.length,
      nuevos: nuevos.length,
      descargados: descargados.length,
      leidos: leidos.length,
      sinPdf: sinPdf.length,
    };
  }, [visitasAdmin]);

  const visitasIndicadoresFiltradas = useMemo(() => {
    const texto = busquedaIndicadores.trim().toLowerCase();
    return indicadores.visitasPeriodo.filter((visita) => {
      const cliente = visita.cliente || {};
      const coincideComuna = !filtroComunaIndicadores || cliente.comuna === filtroComunaIndicadores;
      const coincideTexto = !texto || [
        cliente.cod_cliente,
        cliente.num_gestion,
        cliente.r_social,
        cliente.direccion,
        cliente.comuna,
        visita.tecnico?.nombre,
        visita.tipo_actividad,
        visita.nom_client,
      ].some((valor) => String(valor || '').toLowerCase().includes(texto));

      return coincideComuna && coincideTexto;
    });
  }, [indicadores.visitasPeriodo, filtroComunaIndicadores, busquedaIndicadores]);

  const totalPaginasIndicadores = Math.max(
    1,
    Math.ceil(visitasIndicadoresFiltradas.length / VISITAS_POR_PAGINA)
  );
  const paginaIndicadoresActual = Math.min(paginaIndicadores, totalPaginasIndicadores);
  const visitasIndicadoresPaginadas = useMemo(() => {
    const inicio = (paginaIndicadoresActual - 1) * VISITAS_POR_PAGINA;
    return visitasIndicadoresFiltradas.slice(inicio, inicio + VISITAS_POR_PAGINA);
  }, [visitasIndicadoresFiltradas, paginaIndicadoresActual]);
  const primerResultadoIndicadores = visitasIndicadoresFiltradas.length
    ? ((paginaIndicadoresActual - 1) * VISITAS_POR_PAGINA) + 1
    : 0;
  const ultimoResultadoIndicadores = Math.min(
    paginaIndicadoresActual * VISITAS_POR_PAGINA,
    visitasIndicadoresFiltradas.length
  );

  useEffect(() => {
    setPaginaIndicadores(1);
  }, [periodoIndicadores, filtroMesIndicadores, filtroComunaIndicadores, busquedaIndicadores]);

  const navegarAlertaOperativa = (alerta) => {
    if (alerta.targetTab) {
      setActiveTab(alerta.targetTab);
      return;
    }

    if (alerta.targetId) {
      document.getElementById(alerta.targetId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const abrirAsignacionConFiltro = (filtro) => {
    setFiltroAsignacion(filtro);
    setActiveTab('asignacion');
  };

  const abrirRespaldosPendientes = () => {
    setFiltroRevisionRespaldos('pendientes');
    setActiveTab('respaldos');
  };

  const mostrarDetalleDelMes = () => {
    setPeriodoIndicadores('mes');
    window.requestAnimationFrame(() => {
      document.getElementById('dashboard-detalle-periodo')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const cargarVisitasAdmin = async ({ silent = false } = {}) => {
    if (!silent) setVisitasLoading(true);
    setVisitasError('');
    try {
      const res = await fetch(`${API_BASE}/visitas/admin`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudieron cargar los respaldos.');
      setVisitasAdmin(data);
    } catch (err) {
      setVisitasError(err.message);
    } finally {
      if (!silent) setVisitasLoading(false);
    }
  };

  const cargarClientesAdmin = async ({ silent = false } = {}) => {
    if (!silent) setClientesLoading(true);
    setClientesError('');
    try {
      const res = await fetch(`${API_BASE}/clientes/admin`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudieron cargar los clientes importados.');
      setClientesAdmin(Array.isArray(data) ? data : []);
    } catch (err) {
      setClientesError(err.message);
    } finally {
      if (!silent) setClientesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'respaldos' || activeTab === 'indicadores') {
      cargarVisitasAdmin();
    }
    if (activeTab === 'indicadores') {
      cargarClientesAdmin();
    }
    if (activeTab === 'usuarios' || activeTab === 'configuracion') {
      cargarUsuarios();
    }
    if (activeTab === 'asignacion') {
      cargarUsuarios();
      cargarClientesAdmin();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'respaldos') return undefined;
    const intervalId = window.setInterval(() => {
      cargarVisitasAdmin({ silent: true });
    }, RESPALDOS_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'indicadores') return undefined;
    const intervalId = window.setInterval(() => {
      cargarVisitasAdmin({ silent: true });
      cargarClientesAdmin({ silent: true });
    }, INDICADORES_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [activeTab]);

  const clientesAsignacionFiltrados = useMemo(() => {
    const texto = clienteFiltro.trim().toLowerCase();
    return clientesAdmin.filter((cliente) => {
      const coincideTexto = !texto || [
        cliente.cod_cliente,
        cliente.num_gestion,
        cliente.r_social,
        cliente.comuna,
        cliente.tecnico?.nombre,
      ].some((valor) => String(valor || '').toLowerCase().includes(texto));

      if (!coincideTexto) return false;
      if (filtroComunaAsignacion && cliente.comuna !== filtroComunaAsignacion) return false;
      if (filtroAsignacion === 'sin_asignar') return !tieneTecnicoValido(cliente);
      if (filtroAsignacion === 'asignados') return tieneTecnicoValido(cliente);
      if (filtroAsignacion === 'reprogramados') return cliente.estado === 'reprogramado';
      if (filtroAsignacion === 'revision_admin') return tieneDecisionAdminPendiente(cliente);
      if (filtroAsignacion === 'revisar_rol') return tieneAsignacionNoTecnica(cliente);
      if (filtroAsignacion === 'pendientes') return cliente.estado !== 'finalizado';
      return true;
    }).sort((a, b) => (
      Number(b.prioridad_admin || 0) - Number(a.prioridad_admin || 0) ||
      Number(b.deuda_vencida || 0) - Number(a.deuda_vencida || 0) ||
      String(a.comuna || '').localeCompare(String(b.comuna || '')) ||
      String(a.cod_cliente || '').localeCompare(String(b.cod_cliente || ''))
    ));
  }, [clientesAdmin, clienteFiltro, filtroAsignacion, filtroComunaAsignacion]);

  const handleAsignarTecnico = async (cliente, tecnicoId) => {
    if (!tecnicoId) return;
    const key = `${cliente.cod_cliente}-${cliente.num_gestion}`;
    setAssigningKey(key);
    try {
      const res = await fetch(`${API_BASE}/clientes/${encodeURIComponent(cliente.cod_cliente)}/asignar`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tecnico_id: Number(tecnicoId),
          num_gestion: cliente.num_gestion,
        }),
      });
      await readApiJson(res, 'No se pudo asignar el técnico.');
      await cargarClientesAdmin();
    } catch (err) {
      alert(err.message);
    } finally {
      setAssigningKey('');
    }
  };

  const handleTerminarClienteAdmin = async (cliente) => {
    const key = `${cliente.cod_cliente}-${cliente.num_gestion}`;
    const resultado = cliente.ultima_visita?.resultado_label || cliente.ultima_visita?.resultado_visita || 'la ultima visita';
    const confirmar = window.confirm(
      `Marcar como terminado el caso ${cliente.cod_cliente} / gestion ${cliente.num_gestion}?\n\nResultado tecnico registrado: ${resultado}.\nEl PDF queda disponible como respaldo y el caso saldra de asignacion pendiente.`
    );
    if (!confirmar) return;

    setClosingKey(key);
    try {
      const res = await fetch(`${API_BASE}/clientes/${encodeURIComponent(cliente.cod_cliente)}/terminar-admin`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ num_gestion: cliente.num_gestion }),
      });
      const data = await readApiJson(res, 'No se pudo terminar el caso.');
      setClientesAdmin((prev) => prev.map((item) => (item.id === data.id ? data : item)));
    } catch (err) {
      alert(err.message);
    } finally {
      setClosingKey('');
    }
  };

  const handleCambiarPrioridadCliente = async (cliente, prioridadAdmin) => {
    const key = `${cliente.cod_cliente}-${cliente.num_gestion}`;
    setPriorityKey(key);
    try {
      const res = await fetch(`${API_BASE}/clientes/${encodeURIComponent(cliente.cod_cliente)}/prioridad`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          num_gestion: cliente.num_gestion,
          prioridad_admin: prioridadAdmin ? 1 : 0,
          prioridad_motivo: prioridadAdmin ? 'Prioridad definida por administracion' : '',
        }),
      });
      const data = await readApiJson(res, 'No se pudo actualizar la prioridad del caso.');
      setClientesAdmin((prev) => prev.map((item) => (item.id === data.id ? data : item)));
    } catch (err) {
      alert(err.message);
    } finally {
      setPriorityKey('');
    }
  };

  const handleAsignarComuna = async (e) => {
    e.preventDefault();
    setBulkStatus('');
    if (!bulkComuna || !bulkTecnicoId) {
      setBulkStatus({ ok: false, msg: 'Selecciona una comuna y un técnico.' });
      return;
    }

    setBulkAssigning(true);
    try {
      const res = await fetch(`${API_BASE}/clientes/asignar-comuna`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comuna: bulkComuna,
          tecnico_id: Number(bulkTecnicoId),
          solo_sin_asignar: bulkSoloSinAsignar,
        }),
      });
      const data = await readApiJson(res, 'No se pudo asignar la comuna.');
      setBulkStatus({
        ok: true,
        msg: `${data.asignados} caso(s) asignados en ${data.comuna}.`,
      });
      await cargarClientesAdmin();
    } catch (err) {
      setBulkStatus({ ok: false, msg: err.message });
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleDescargarPdf = async (visita) => {
    try {
      const res = await fetch(`${API_BASE}/visitas/${visita.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo descargar el PDF.'));
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cliente = visita.cliente?.cod_cliente || 'cliente';
      const gestion = visita.cliente?.num_gestion || 'gestion';
      a.href = url;
      a.download = `visita_${cliente}_${gestion}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      const descargadoAt = new Date().toISOString();
      setVisitasAdmin((prev) => prev.map((item) => (
        item.id === visita.id
          ? { ...item, pdf_descargado_at: item.pdf_descargado_at || descargadoAt }
          : item
      )));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleMarcarVistoPdf = async (visita, visto) => {
    try {
      const res = await fetch(`${API_BASE}/visitas/admin/${visita.id}/revision`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ visto }),
      });
      if (res.status === 404) {
        throw new Error('La API no tiene activa la ruta para marcar lectura de respaldos. Reinicia/actualiza el backend v2.1.');
      }
      const data = await readApiJson(res, 'No se pudo actualizar la revision del respaldo.');
      setVisitasAdmin((prev) => prev.map((item) => (item.id === visita.id ? data : item)));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDescargarPdfZip = async (e) => {
    e.preventDefault();
    setPdfZipStatus('');
    if (pdfZipDesde && pdfZipHasta && pdfZipDesde > pdfZipHasta) {
      setPdfZipStatus({ ok: false, msg: 'La fecha desde no puede ser mayor que la fecha hasta.' });
      return;
    }

    setPdfZipLoading(true);
    try {
      const params = new URLSearchParams();
      if (pdfZipDesde) params.set('fecha_desde', pdfZipDesde);
      if (pdfZipHasta) params.set('fecha_hasta', pdfZipHasta);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API_BASE}/visitas/admin/pdf-zip${query}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo descargar el ZIP de respaldos.'));
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `respaldos_pdf_${pdfZipDesde || 'inicio'}_${pdfZipHasta || 'hoy'}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      const descargadoAt = new Date().toISOString();
      setVisitasAdmin((prev) => prev.map((visita) => {
        if (!visita.pdf_disponible || visita.pdf_descargado_at) return visita;
        const fechaBase = visita.fech_termino || visita.fecha_ini;
        const fecha = fechaBase ? new Date(fechaBase) : null;
        if (!fecha || Number.isNaN(fecha.getTime())) return visita;
        const dia = fecha.toISOString().slice(0, 10);
        if (pdfZipDesde && dia < pdfZipDesde) return visita;
        if (pdfZipHasta && dia > pdfZipHasta) return visita;
        return { ...visita, pdf_descargado_at: descargadoAt };
      }));
      setPdfZipStatus({ ok: true, msg: 'ZIP generado correctamente.' });
    } catch (err) {
      setPdfZipStatus({ ok: false, msg: err.message });
    } finally {
      setPdfZipLoading(false);
    }
  };

  const handleDescargarInformeMetricas = async () => {
    setInformeMetricasStatus('');
    setInformeMetricasLoading(true);
    try {
      const params = new URLSearchParams({ mes: filtroMesIndicadores });
      const res = await fetch(`${API_BASE}/visitas/admin/informe-metricas?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo generar el informe de metricas.'));
      }
      const blob = await res.blob();
      if (blob.type !== 'application/pdf' || blob.size === 0) {
        throw new Error('El servidor no entrego un informe PDF valido.');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `informe_metricas_ot_cobranza_${filtroMesIndicadores}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      setInformeMetricasStatus({
        ok: true,
        msg: `Informe de ${indicadoresV21.mesActualLabel} descargado correctamente.`,
      });
    } catch (err) {
      setInformeMetricasStatus({ ok: false, msg: err.message });
    } finally {
      setInformeMetricasLoading(false);
    }
  };

  const cargarTiposActividad = async () => {
    try {
      const res = await fetch(`${API_BASE}/actividades/?solo_activas=false`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudieron cargar los tipos de actividad.');
      setTiposActividad(data);
    } catch (err) {
      setActividadStatus({ ok: false, msg: err.message });
    }
  };

  useEffect(() => {
    if (activeTab === 'configuracion') {
      cargarTiposActividad();
    }
  }, [activeTab]);

  const handleCrearActividad = async (e) => {
    e.preventDefault();
    setActividadStatus('');
    try {
      const res = await fetch(`${API_BASE}/actividades/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nombre: actividadNueva }),
      });
      const data = await readApiJson(res, 'No se pudo crear la actividad.');
      setTiposActividad((prev) => [...prev, data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setActividadNueva('');
      setActividadStatus({ ok: true, msg: 'Tipo de actividad agregado.' });
    } catch (err) {
      setActividadStatus({ ok: false, msg: err.message });
    }
  };

  const handleToggleActividad = async (actividad) => {
    setActividadStatus('');
    try {
      const res = await fetch(`${API_BASE}/actividades/${actividad.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activo: !actividad.activo }),
      });
      const data = await readApiJson(res, 'No se pudo actualizar la actividad.');
      setTiposActividad((prev) => prev.map((item) => item.id === data.id ? data : item));
    } catch (err) {
      setActividadStatus({ ok: false, msg: err.message });
    }
  };

  const handleEliminarActividad = async (actividad) => {
    setActividadStatus('');
    try {
      const res = await fetch(`${API_BASE}/actividades/${actividad.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      await readApiJson(res, 'No se pudo eliminar la actividad.');
      setTiposActividad((prev) => prev.filter((item) => item.id !== actividad.id));
    } catch (err) {
      setActividadStatus({ ok: false, msg: err.message });
    }
  };

  const handleDescargarPlantilla = async () => {
    try {
      const res = await fetch(`${API_BASE}/clientes/plantilla`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo descargar la plantilla'));
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_importacion_clientes.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar la plantilla: ' + err.message);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!file) {
      alert('Selecciona un archivo Excel primero.');
      return;
    }
    if (!validationResult || validationResult.validas === 0) {
      alert('Primero revisa el archivo y confirma que existan filas correctas para importar.');
      return;
    }

    setIsUploading(true);
    setUploadStatus('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/clientes/importar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        if (isAuthError(res, data)) notifySessionExpired();
        setUploadStatus({ ok: false, msg: formatApiError(res, data, 'Error desconocido') });
      } else {
        setUploadStatus({
          ok: true,
          msg: `Importación exitosa sin duplicar. Nuevos: ${data.importados} | Actualizados: ${data.actualizados} | Omitidos: ${data.omitidos || 0} | Duplicados en archivo: ${data.duplicadas_archivo || 0}. La deuda vencida se actualiza cuando la gestión ya existe.`,
        });
        setFile(null);
        setValidationResult(null);
        form.reset();
      }
    } catch (err) {
      setUploadStatus({ ok: false, msg: 'Error de conexión: ' + err.message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setValidationResult(null);
    setUploadStatus('');
  };

  const handleValidateImport = async () => {
    if (!file) {
      alert('Selecciona un archivo Excel primero.');
      return;
    }

    setIsValidating(true);
    setUploadStatus('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/clientes/validar-importacion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        if (isAuthError(res, data)) notifySessionExpired();
        setValidationResult(null);
        setUploadStatus({ ok: false, msg: formatApiError(res, data, 'No se pudo revisar el archivo.') });
      } else {
        setValidationResult(data);
        setUploadStatus({
          ok: data.validas > 0,
          msg: data.validas > 0
            ? `Revisión lista. Filas correctas: ${data.validas} | Observadas: ${data.observadas}`
            : 'El archivo no tiene filas correctas para importar.',
        });
      }
    } catch (err) {
      setValidationResult(null);
      setUploadStatus({ ok: false, msg: 'Error de conexión: ' + err.message });
    } finally {
      setIsValidating(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const res = await fetch(`${API_BASE}/usuarios/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre: formData.get('nombre'),
          email: formData.get('email'),
          rol: formData.get('rol'),
          activo: true,
          is_verified: false,
        }),
      });
      const data = await readApiJson(res, 'No se pudo crear el usuario.');
      form.reset();
      setTemporaryPassword({
        email: data.usuario?.email || formData.get('email'),
        temporary_password: data.temporary_password,
      });
      await cargarUsuarios();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser) return;
    if (isSuperadmin(selectedUser.rol)) {
      alert('El rol superadmin no se modifica desde este panel.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/usuarios/${selectedUser.id}/rol`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rol: newRole }),
      });
      const data = await readApiJson(res, 'No se pudo actualizar el rol.');
      setUsuarios((prev) => prev.map((usuario) => (
        usuario.id === selectedUser.id ? data : usuario
      )));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleActive = (id) => {
    setUsuarios((prev) => prev.map((usuario) => (
      usuario.id === id && !isSuperadmin(usuario.rol)
        ? { ...usuario, activo: !usuario.activo }
        : usuario
    )));
  };

  const handleCopyTemporaryPassword = async () => {
    if (!temporaryPassword?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(temporaryPassword.temporary_password);
      alert('Clave temporal copiada al portapapeles.');
    } catch {
      alert('No se pudo copiar automáticamente. Puedes seleccionarla manualmente.');
    }
  };

  const handleDeleteUser = async (usuario) => {
    if (isSuperadmin(usuario.rol)) {
      alert('El superadmin no se puede eliminar desde este panel.');
      return;
    }
    if (usuario.id === currentUser?.id) {
      alert('No puedes eliminar tu propio usuario.');
      return;
    }
    if (!window.confirm(`Eliminar usuario ${usuario.nombre}? Esta accion no se puede deshacer.`)) {
      return;
    }

    setDeletingUserId(usuario.id);
    try {
      const res = await fetch(`${API_BASE}/usuarios/${usuario.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      await readApiJson(res, 'No se pudo eliminar el usuario.');
      setUsuarios((prev) => prev.filter((item) => item.id !== usuario.id));
      if (selectedUserId === String(usuario.id)) {
        setSelectedUserId('');
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingUserId('');
    }
  };

  const handleStartMfaSetup = async () => {
    setMfaStatus('');
    setMfaCode('');
    try {
      const data = await setupTotp();
      setMfaSetup(data);
    } catch (err) {
      setMfaStatus({ ok: false, msg: err.message });
    }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault();
    setMfaStatus('');
    try {
      const data = await confirmTotp(mfaCode);
      setMfaEnabled(true);
      setMfaSetup(null);
      setMfaCode('');
      setMfaStatus({ ok: true, msg: data.message || 'Doble factor activado correctamente.' });
      setSession(getToken(), data.usuario);
    } catch (err) {
      setMfaStatus({ ok: false, msg: err.message });
    }
  };

  const handleResetPassword = async (usuarioObjetivo = selectedUser) => {
    if (!usuarioObjetivo) return;
    if (isSuperadmin(usuarioObjetivo.rol)) {
      alert('El acceso del superadmin no se reinicia desde este panel.');
      return;
    }
    setTemporaryPassword(null);
    setResetLoading(true);
    setResetPasswordUserId(usuarioObjetivo.id);

    try {
      const res = await fetch(`${API_BASE}/usuarios/${usuarioObjetivo.id}/reset-temporal-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudo reiniciar el acceso.');
      setTemporaryPassword(data);
      await cargarUsuarios();
    } catch (err) {
      alert(err.message);
    } finally {
      setResetLoading(false);
      setResetPasswordUserId('');
    }
  };

  const handleResetMfaUser = async (usuario) => {
    if (isSuperadmin(usuario.rol)) {
      alert('El doble factor del superadmin no se reinicia desde este panel.');
      return;
    }
    if (!window.confirm(`¿Re-vincular doble factor de ${usuario.nombre}? El usuario deberá escanear un nuevo QR.`)) {
      return;
    }

    setResetMfaUserId(usuario.id);
    try {
      const res = await fetch(`${API_BASE}/usuarios/${usuario.id}/reset-mfa`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await readApiJson(res, 'No se pudo reiniciar el doble factor.');
      setUsuarios((prev) => prev.map((item) => (
        item.id === usuario.id ? { ...item, totp_enabled: false } : item
      )));
      alert(data.message || 'Doble factor reiniciado correctamente.');
    } catch (err) {
      alert(err.message);
    } finally {
      setResetMfaUserId('');
    }
  };

  return (
    <div className="gv-fade-in">
      <div className="admin-hero">
        <div>
          <span className="admin-hero__eyebrow">Administración</span>
          <h2>Panel de Administración</h2>
          <p>Gestión centralizada de usuarios, bases de clientes y operaciones de terreno.</p>
        </div>
      </div>

      <div className="admin-tabs" role="tablist" aria-label="Secciones de administración">
        {tabs
          .filter((tab) => tab.id !== 'configuracion' || canManageConfig)
          .map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`admin-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
      </div>

      {activeTab === 'indicadores' && (
        <section className="gv-card gv-card--accent admin-card admin-card--wide">
          <div className="admin-card__heading">
            <div>
              <span className="ops-heading__eyebrow">Vista operativa</span>
              <h3>Control diario de terreno</h3>
              <p>Indicadores por día, semana o mes para seguimiento de técnicos, acciones realizadas, cierres efectivos, respaldos PDF y evidencia fotográfica.</p>
            </div>
            <div className="ops-heading__actions">
              <label className="ops-month-filter">
                <span>Mes dashboard</span>
                <select
                  className="gv-select"
                  value={filtroMesIndicadores}
                  onChange={(e) => {
                    setFiltroMesIndicadores(e.target.value);
                    setPeriodoIndicadores('mes');
                  }}
                >
                  {mesesIndicadores.map((mes) => (
                    <option key={mes.value} value={mes.value}>{mes.label}</option>
                  ))}
                </select>
              </label>
              <div className="ops-heading__tools">
                <div className="ops-segmented ops-segmented--heading" role="group" aria-label="Periodo del detalle">
                  {[
                    { id: 'dia', label: 'Día' },
                    { id: 'semana', label: 'Semana' },
                    { id: 'mes', label: 'Mes' },
                  ].map((periodo) => (
                    <button
                      key={periodo.id}
                      type="button"
                      aria-pressed={periodoIndicadores === periodo.id}
                      className={periodoIndicadores === periodo.id ? 'active' : ''}
                      onClick={() => setPeriodoIndicadores(periodo.id)}
                    >
                      {periodo.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="gv-btn gv-btn--secondary ops-report-button"
                  onClick={handleDescargarInformeMetricas}
                  disabled={informeMetricasLoading || visitasLoading}
                >
                  {informeMetricasLoading ? 'Generando...' : 'Descargar informe'}
                </button>
              </div>
            </div>
          </div>

          {visitasError && <div className="gv-alert gv-alert--error">{visitasError}</div>}
          {informeMetricasStatus && (
            <div className={`gv-alert ${informeMetricasStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}>
              {informeMetricasStatus.msg}
            </div>
          )}

          <div className="ops-v21-hero">
            <div className="ops-v21-summary">
              <div className="ops-v21-title">
                <span>Resumen mensual</span>
                <h4>{indicadoresV21.mesActualLabel}</h4>
                <p>Carga actual, acciones tecnicas, cierres efectivos y comparativa contra {indicadoresV21.mesAnteriorLabel}.</p>
              </div>
              <div className="ops-v21-metrics">
                <button type="button" onClick={() => abrirAsignacionConFiltro('pendientes')}>
                  <span>Carga actual</span>
                  <strong>{indicadoresV21.clientesActivos}</strong>
                  <small>Ver casos activos</small>
                </button>
                <button type="button" onClick={() => abrirAsignacionConFiltro('sin_asignar')}>
                  <span>Sin asignar</span>
                  <strong>{indicadoresV21.sinAsignarActuales}</strong>
                  <small>Ir a asignación</small>
                </button>
                <button type="button" onClick={() => abrirAsignacionConFiltro('pendientes')}>
                  <span>Pendientes de cierre</span>
                  <strong>{indicadoresV21.pendientesAsignados}</strong>
                  <small>Revisar carga abierta</small>
                </button>
                <button type="button" onClick={mostrarDetalleDelMes}>
                  <span>Cierres del mes</span>
                  <strong>{indicadoresV21.cierresMes}</strong>
                  <small>{indicadoresV21.intentosMes} intentos registrados</small>
                </button>
                <button type="button" onClick={abrirRespaldosPendientes}>
                  <span>PDF sin revisar</span>
                  <strong>{indicadoresV21.respaldosSinRevisar}</strong>
                  <small>Ir a respaldos</small>
                </button>
              </div>
              <div className="ops-v21-compare">
                {indicadoresV21.comparativas.map((item) => (
                  <div key={item.id}>
                    <span>{item.label}</span>
                    <strong>{item.actual}</strong>
                    <small>
                      {item.diferencia > 0 ? '+' : ''}{item.diferencia} frente a {indicadoresV21.mesAnteriorLabel} ({item.previo})
                    </small>
                  </div>
                ))}
              </div>
            </div>

            <div className="ops-v21-chart">
              <div className="ops-panel__header">
                <div>
                  <h4>Productividad {vistaProductividad === 'diaria' ? 'diaria' : 'semanal'}</h4>
                  <p>Acciones de terreno, PDFs y revisiones del mes seleccionado.</p>
                </div>
                <div className="ops-v21-chart__controls">
                  <div className="ops-segmented ops-segmented--compact" role="group" aria-label="Agrupación del gráfico">
                    {[
                      { id: 'diaria', label: 'Diaria' },
                      { id: 'semanal', label: 'Semanal' },
                    ].map((vista) => (
                      <button
                        key={vista.id}
                        type="button"
                        aria-pressed={vistaProductividad === vista.id}
                        className={vistaProductividad === vista.id ? 'active' : ''}
                        onClick={() => setVistaProductividad(vista.id)}
                      >
                        {vista.label}
                      </button>
                    ))}
                  </div>
                  <span className="ops-v21-chip">{indicadoresV21.mesActualLabel}</span>
                </div>
              </div>
              <div
                className={`ops-v21-bars${vistaProductividad === 'semanal' ? ' ops-v21-bars--weekly' : ''}`}
                aria-label={`Productividad ${vistaProductividad === 'diaria' ? 'diaria' : 'semanal'} de ${indicadoresV21.mesActualLabel}`}
              >
                {productividadGrafico.datos.map((dato) => (
                  <div
                    key={dato.clave}
                    className="ops-v21-day"
                    tabIndex="0"
                    aria-label={`${dato.descripcion}: ${dato.acciones} acciones, ${dato.cerradas} cierres, ${dato.pdf} PDF y ${dato.revisados} revisados`}
                    title={`${dato.descripcion}: ${dato.acciones} acciones, ${dato.cerradas} cierres, ${dato.pdf} PDF, ${dato.revisados} revisados`}
                  >
                    <span
                      className="ops-v21-day__bar ops-v21-day__bar--closed"
                      style={{ height: `${Math.max((dato.acciones / productividadGrafico.maximo) * 100, dato.acciones ? 12 : 2)}%` }}
                    />
                    <span
                      className="ops-v21-day__bar ops-v21-day__bar--pdf"
                      style={{ height: `${Math.max((dato.pdf / productividadGrafico.maximo) * 100, dato.pdf ? 12 : 2)}%` }}
                    />
                    <span
                      className="ops-v21-day__bar ops-v21-day__bar--review"
                      style={{ height: `${Math.max((dato.revisados / productividadGrafico.maximo) * 100, dato.revisados ? 12 : 2)}%` }}
                    />
                    <small>{dato.etiqueta}</small>
                    {vistaProductividad === 'semanal' && <b>{dato.acciones}</b>}
                  </div>
                ))}
              </div>
              <div className="ops-v21-legend">
                <span><i className="ops-v21-dot ops-v21-dot--closed" />Acciones</span>
                <span><i className="ops-v21-dot ops-v21-dot--pdf" />PDF</span>
                <span><i className="ops-v21-dot ops-v21-dot--review" />Revisados</span>
              </div>
              <div className="ops-v21-month-trend">
                <div className="ops-v21-month-trend__header">
                  <div>
                    <h5>Tendencia mensual acumulada</h5>
                    <p>Compara acciones realizadas, cierres efectivos, PDF generados y respaldos revisados.</p>
                  </div>
                </div>
                <div className="ops-v21-month-trend__explanation" aria-label="Explicación de métricas mensuales">
                  <span><i className="ops-v21-dot ops-v21-dot--closed" />Barras azules: acciones</span>
                  <span><i className="ops-v21-dot ops-v21-dot--pdf" />Barras verdes: PDF</span>
                  <span><i className="ops-v21-dot ops-v21-dot--review" />Barras celestes: revisados</span>
                  <span>Cierres efectivos se muestran en el detalle de cada mes.</span>
                </div>
                <div className="ops-v21-month-trend__bars" aria-label="Comparación mensual acumulada">
                  {indicadoresV21.tendenciaMensual.map((mes) => (
                    <div key={mes.clave} className="ops-v21-month">
                      <div className="ops-v21-month__bars">
                        <span
                          className="ops-v21-month__bar ops-v21-month__bar--closed"
                          style={{ height: `${Math.max((mes.acciones / indicadoresV21.maxTendenciaMensual) * 100, mes.acciones ? 12 : 2)}%` }}
                          title={`${mes.label}: ${mes.acciones} acciones`}
                        />
                        <span
                          className="ops-v21-month__bar ops-v21-month__bar--pdf"
                          style={{ height: `${Math.max((mes.pdf / indicadoresV21.maxTendenciaMensual) * 100, mes.pdf ? 12 : 2)}%` }}
                          title={`${mes.label}: ${mes.pdf} PDF`}
                        />
                        <span
                          className="ops-v21-month__bar ops-v21-month__bar--review"
                          style={{ height: `${Math.max((mes.revisados / indicadoresV21.maxTendenciaMensual) * 100, mes.revisados ? 12 : 2)}%` }}
                          title={`${mes.label}: ${mes.revisados} revisados`}
                        />
                      </div>
                      <strong>{mes.label}</strong>
                      <dl className="ops-v21-month__metrics" aria-label={`Detalle de ${mes.label}`}>
                        <div><dt>Acciones</dt><dd>{mes.acciones}</dd></div>
                        <div><dt>Cierres</dt><dd>{mes.cerradas}</dd></div>
                        <div><dt>PDF</dt><dd>{mes.pdf}</dd></div>
                        <div><dt>Revisados</dt><dd>{mes.revisados}</dd></div>
                      </dl>
                    </div>
                  ))}
                  {indicadoresV21.tendenciaMensual.length === 0 && (
                    <p className="import-empty">Sin meses históricos para comparar aún.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="ops-v21-insights">
            <div className="ops-v21-funnel">
              <div className="ops-panel__header">
                <div>
                  <h4>Embudo operativo mensual</h4>
                  <p>Avance referencial: carga actual, acciones tecnicas, cierres efectivos, PDF y revision.</p>
                </div>
              </div>
              <div className="ops-v21-funnel__list">
                {indicadoresV21.embudoMensual.map((item) => {
                  const ancho = Math.max((item.value / indicadoresV21.maxEmbudo) * 100, item.value ? 10 : 3);
                  return (
                    <div key={item.id} className="ops-v21-funnel__row">
                      <div>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                      <div className="ops-v21-funnel__meta">
                        <small>{item.referencia}</small>
                      </div>
                      <div className="ops-v21-funnel__track">
                        <span style={{ width: `${ancho}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ops-v21-alerts">
              <div className="ops-panel__header">
                <div>
                  <h4>Alertas operativas</h4>
                  <p>Senales que requieren seguimiento administrativo.</p>
                </div>
              </div>
              {indicadoresV21.alertasOperativas.length > 0 ? (
                <div className="ops-v21-alerts__list">
                  {indicadoresV21.alertasOperativas.map((alerta) => (
                    <button
                      key={`${alerta.titulo}-${alerta.detalle}`}
                      type="button"
                      className={`ops-v21-alert ops-v21-alert--${alerta.tipo}`}
                      onClick={() => navegarAlertaOperativa(alerta)}
                    >
                      <div className="ops-v21-alert__top">
                        <strong>{alerta.titulo}</strong>
                        <span className={`ops-v21-priority ops-v21-priority--${alerta.tipo}`}>{alerta.prioridadLabel}</span>
                      </div>
                      <span>{alerta.detalle}</span>
                      <em>{alerta.accion}</em>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="import-empty">Sin alertas operativas relevantes para este momento.</p>
              )}
            </div>
          </div>

          <div className="ops-v21-detail-grid">
            <div id="dashboard-carga-tecnico" className="ops-v21-workload">
              <div className="ops-panel__header">
                <div>
                  <h4>Carga por tecnico v2.1</h4>
                  <p>Pendientes actuales versus acciones tecnicas y cierres efectivos del mes.</p>
                </div>
              </div>
              <div className="ops-v21-workload__summary">
                <div>
                  <span>Tecnicos con carga</span>
                  <strong>{indicadoresV21.resumenTecnicos.total}</strong>
                </div>
                <div>
                  <span>Pendientes</span>
                  <strong>{indicadoresV21.resumenTecnicos.pendientes}</strong>
                </div>
                <div>
                  <span>Acciones mes</span>
                  <strong>{indicadoresV21.resumenTecnicos.acciones}</strong>
                  <small>{indicadoresV21.resumenTecnicos.cerradas} cierres, {indicadoresV21.resumenTecnicos.intentos} intentos</small>
                </div>
                <div>
                  <span>Requieren atencion</span>
                  <strong>{indicadoresV21.resumenTecnicos.atencion}</strong>
                </div>
              </div>
              <div className="ops-v21-workload__list">
                {indicadoresV21.tecnicosOperacion.map((tecnico) => (
                  <div key={tecnico.id || tecnico.nombre} className="ops-v21-workload__row">
                    <div className="ops-v21-workload__meta">
                      <div className="ops-v21-row-head">
                        <strong>{tecnico.nombre}</strong>
                        <span className={`ops-v21-status ops-v21-status--${tecnico.riesgo}`}>{tecnico.riesgoLabel}</span>
                      </div>
                      <div className="ops-v21-mini-metrics">
                        <span><b>{tecnico.pendientes}</b> pendientes</span>
                        <span><b>{tecnico.acciones}</b> acciones</span>
                        <span><b>{tecnico.cerradas}</b> cierres</span>
                        <span><b>{tecnico.intentos}</b> intentos</span>
                        <span><b>{tecnico.coberturaRevision}%</b> revision PDF</span>
                        <span><b>{tecnico.presionOperativa}</b> pend/accion</span>
                      </div>
                    </div>
                    <div className="ops-v21-workload__bars">
                      <span
                        className="ops-v21-workload__bar ops-v21-workload__bar--pending"
                        style={{ width: `${Math.max((tecnico.pendientes / indicadoresV21.maxTecnicoCarga) * 100, tecnico.pendientes ? 8 : 2)}%` }}
                      />
                      <span
                        className="ops-v21-workload__bar ops-v21-workload__bar--closed"
                        style={{ width: `${Math.max((tecnico.acciones / indicadoresV21.maxTecnicoCarga) * 100, tecnico.acciones ? 8 : 2)}%` }}
                      />
                    </div>
                    <small className="ops-v21-action">{tecnico.accion}</small>
                  </div>
                ))}
                {indicadoresV21.tecnicosOperacion.length === 0 && (
                  <p className="import-empty">Sin carga por tecnico para mostrar.</p>
                )}
              </div>
            </div>

            <div className="ops-v21-communes">
              <div className="ops-panel__header">
                <div>
                  <h4>Concentracion por comuna</h4>
                  <p>Casos activos, sin asignar y cierres efectivos del mes.</p>
                </div>
              </div>
              <div className="ops-v21-workload__summary ops-v21-workload__summary--communes">
                <div>
                  <span>Comunas activas</span>
                  <strong>{indicadoresV21.resumenComunas.total}</strong>
                </div>
                <div>
                  <span>Casos activos</span>
                  <strong>{indicadoresV21.resumenComunas.activos}</strong>
                </div>
                <div>
                  <span>Sin asignar</span>
                  <strong>{indicadoresV21.resumenComunas.sinAsignar}</strong>
                </div>
                <div>
                  <span>Cobertura</span>
                  <strong>{indicadoresV21.resumenComunas.coberturaAsignacion}%</strong>
                </div>
              </div>
              <div className="ops-v21-communes__list">
                {indicadoresV21.porComuna.map((comuna) => (
                  <div key={comuna.comuna} className="ops-v21-communes__row">
                    <div>
                      <div className="ops-v21-row-head">
                        <strong>{comuna.comuna}</strong>
                        <span className={`ops-v21-status ops-v21-status--${comuna.riesgo}`}>{comuna.riesgoLabel}</span>
                      </div>
                      <div className="ops-v21-mini-metrics">
                        <span><b>{comuna.activos}</b> activos</span>
                        <span><b>{comuna.sinAsignar}</b> sin asignar</span>
                        <span><b>{comuna.coberturaAsignacion}%</b> asignacion</span>
                        <span><b>{comuna.cerradas}</b> cierres</span>
                      </div>
                    </div>
                    <div className="ops-v21-communes__track">
                      <span style={{ width: `${Math.max((comuna.activos / indicadoresV21.maxComunaCarga) * 100, comuna.activos ? 8 : 2)}%` }} />
                    </div>
                    <small className="ops-v21-action">{comuna.accion}</small>
                  </div>
                ))}
                {indicadoresV21.porComuna.length === 0 && (
                  <p className="import-empty">Sin comunas activas para mostrar.</p>
                )}
              </div>
            </div>
          </div>

          <div id="dashboard-detalle-periodo" className="ops-v21-period-intro">
            <div>
              <span>Detalle complementario</span>
              <h4>{indicadores.periodoLabel}</h4>
              <p>Lectura filtrada por día, semana o mes para revisar actividad y últimas visitas.</p>
            </div>
            <span className="ops-v21-period-badge">
              Datos de {indicadores.periodoLabel}
            </span>
          </div>

          <div className="ops-kpi-grid ops-kpi-grid--compact">
            <div className="ops-kpi">
              <span>Acciones del periodo</span>
              <strong>{indicadores.total}</strong>
              <small>Total histórico: {indicadores.totalGeneral}</small>
            </div>
            <div className="ops-kpi ops-kpi--ok">
              <span>PDF disponibles</span>
              <strong>{indicadores.coberturaPdf}%</strong>
              <small>{indicadores.conPdf} respaldos generados</small>
            </div>
            <div className="ops-kpi">
              <span>Con evidencia</span>
              <strong>{indicadores.coberturaFotos}%</strong>
              <small>{indicadores.fotosTotales} fotografías guardadas</small>
            </div>
            <div className="ops-kpi ops-kpi--warn">
              <span>Casos pendientes actuales</span>
              <strong>{indicadores.pendientesAsignados}</strong>
              <small>No se restan de las acciones del período</small>
            </div>
            <div className="ops-kpi ops-kpi--activity">
              <span>Actividad principal</span>
              <strong>{indicadores.actividadPrincipal?.total || 0}</strong>
              <small>{indicadores.actividadPrincipal?.actividad || 'Sin actividad registrada'}</small>
            </div>
          </div>

          {visitasLoading && <p className="import-empty">Cargando indicadores operativos...</p>}

          {!visitasLoading && indicadores.total === 0 && indicadores.pendientesAsignados === 0 ? (
            <p className="import-empty">Aún no hay visitas registradas ni casos asignados pendientes. Cuando los técnicos guarden visitas, este panel mostrará la operación.</p>
          ) : (
            <div className="ops-dashboard ops-dashboard--detail">
              <div className="ops-panel">
                <h4>Carga por técnico</h4>
                <div className="ops-ranking">
                  {indicadores.porTecnico.map((tecnico) => (
                    <div key={tecnico.id || tecnico.nombre} className="ops-ranking__row">
                      <div>
                        <strong>{tecnico.nombre}</strong>
                        <span>{tecnico.total} acciones · {tecnico.pdf} PDF · {tecnico.fotos} fotos</span>
                        <small>{tecnico.pendientes} pendiente(s) de cierre</small>
                      </div>
                      <em>{tecnico.pendientes}</em>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ops-panel">
                <div className="ops-panel__header ops-panel__header--metric">
                  <div>
                    <h4>Distribución por actividad</h4>
                    <p>
                      Las categorías visibles suman las {indicadores.total} acciones del período.
                      {!mostrarTodasActividades && indicadores.tiposActividadAgrupados > 0
                        ? ` ${indicadores.tiposActividadAgrupados} tipos menores están agrupados.`
                        : ''}
                    </p>
                  </div>
                  <div className="ops-panel__metric-actions">
                    <strong>{indicadores.total} en total</strong>
                    {indicadores.tiposActividadAgrupados > 0 && (
                      <button
                        type="button"
                        className="gv-btn gv-btn--ghost gv-btn--sm"
                        aria-expanded={mostrarTodasActividades}
                        onClick={() => setMostrarTodasActividades((actual) => !actual)}
                      >
                        {mostrarTodasActividades ? 'Ver resumen' : 'Ver detalle'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="ops-bars">
                  {(mostrarTodasActividades
                    ? indicadores.porActividad
                    : indicadores.porActividadResumen
                  ).map((actividad) => {
                    const ancho = indicadores.total ? Math.max((actividad.total / indicadores.total) * 100, 8) : 0;
                    return (
                      <div
                        key={actividad.actividad}
                        className={`ops-bar${actividad.agrupada ? ' ops-bar--grouped' : ''}`}
                      >
                        <div>
                          <span>{actividad.actividad}</span>
                          <strong>{actividad.total}</strong>
                        </div>
                        <div className="ops-bar__track">
                          <span style={{ width: `${ancho}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="ops-panel ops-panel--wide">
                <div className="ops-panel__header">
                  <div>
                    <h4>Últimas visitas recibidas</h4>
                    <p>
                      {visitasIndicadoresFiltradas.length} resultado(s) dentro del período seleccionado.
                      {' '}Se muestran 20 por página.
                    </p>
                  </div>
                </div>
                <div className="ops-filters">
                  <div className="gv-form-group">
                    <label className="gv-label">Comuna</label>
                    <select
                      className="gv-select"
                      value={filtroComunaIndicadores}
                      onChange={(event) => setFiltroComunaIndicadores(event.target.value)}
                    >
                      <option value="">Todas las comunas</option>
                      {comunasIndicadores.map((comuna) => (
                        <option key={comuna} value={comuna}>{comuna}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gv-form-group">
                    <label className="gv-label">Buscar trabajo realizado</label>
                    <input
                      type="search"
                      className="gv-input"
                      value={busquedaIndicadores}
                      onChange={(event) => setBusquedaIndicadores(event.target.value)}
                      placeholder="Cliente, gestión, técnico, actividad o dirección"
                    />
                  </div>
                </div>
                <div className="ops-recent">
                  {visitasIndicadoresPaginadas.map((visita) => (
                    <div key={visita.id} className="ops-recent__row">
                      <div className="ops-recent__content">
                        <div className="ops-recent__title">
                          <div>
                            <strong>{visita.cliente?.cod_cliente} · Gestión {visita.cliente?.num_gestion}</strong>
                            <span>{visita.cliente?.r_social || 'Sin razón social'}</span>
                          </div>
                          <time>{formatearFechaHora(visita.fech_termino || visita.fecha_ini)}</time>
                        </div>
                        <small className="ops-recent__meta">
                          {visita.cliente?.comuna || 'Sin comuna'} · {visita.tecnico?.nombre || 'Sin técnico'} · {visita.tipo_actividad || 'Sin actividad'}
                        </small>
                        <div className="ops-recent__chips">
                          <em className={visita.pdf_disponible ? 'ok' : 'warn'}>
                            {visita.pdf_disponible ? 'PDF listo' : 'Sin PDF'}
                          </em>
                          <em className={visita.pdf_visto_at ? 'ok' : 'warn'}>
                            {visita.pdf_visto_at ? 'Revisado' : 'Sin revisar'}
                          </em>
                          <em>{visita.fotos?.length || 0} foto(s)</em>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="gv-btn gv-btn--ghost gv-btn--sm"
                        disabled={!visita.pdf_disponible}
                        onClick={() => handleDescargarPdf(visita)}
                      >
                        PDF
                      </button>
                    </div>
                  ))}
                  {visitasIndicadoresFiltradas.length === 0 && (
                    <p className="import-empty">No hay visitas que coincidan con esos filtros.</p>
                  )}
                </div>
                {visitasIndicadoresFiltradas.length > 0 && (
                  <nav className="ops-pagination" aria-label="Paginación de últimas visitas">
                    <p>
                      Mostrando {primerResultadoIndicadores}-{ultimoResultadoIndicadores} de{' '}
                      {visitasIndicadoresFiltradas.length}
                    </p>
                    <div>
                      <button
                        type="button"
                        className="gv-btn gv-btn--ghost gv-btn--sm"
                        disabled={paginaIndicadoresActual === 1}
                        onClick={() => setPaginaIndicadores((pagina) => Math.max(1, pagina - 1))}
                      >
                        Anterior
                      </button>
                      <span aria-live="polite">
                        Página {paginaIndicadoresActual} de {totalPaginasIndicadores}
                      </span>
                      <button
                        type="button"
                        className="gv-btn gv-btn--ghost gv-btn--sm"
                        disabled={paginaIndicadoresActual === totalPaginasIndicadores}
                        onClick={() => setPaginaIndicadores((pagina) => Math.min(totalPaginasIndicadores, pagina + 1))}
                      >
                        Siguiente
                      </button>
                    </div>
                  </nav>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {activeTab === 'carga' && (
        <section className="gv-card gv-card--accent admin-card admin-card--wide">
          <p className="gv-section-title">Carga de Base de Datos</p>

          <div className="import-panel">
            <div className="import-panel__header">
              <div>
                <span className="import-panel__eyebrow">Plantilla oficial</span>
                <h3 className="import-panel__title">Importación de clientes</h3>
                <p className="import-panel__copy">
                  Usa la estructura validada para cargar bases operativas sin perder trazabilidad.
                </p>
              </div>
              <button onClick={handleDescargarPlantilla} className="gv-btn gv-btn--outline">
                Descargar plantilla
              </button>
            </div>

            <div className="import-panel__section">
              <span className="import-panel__label">Columnas requeridas</span>
              <div className="import-panel__chips">
                {columnas.map((columna) => (
                  <code key={columna} className="import-chip">{columna}</code>
                ))}
              </div>
            </div>

            <div className="import-rule">
              <div>
                <span className="import-rule__label">Regla de identificación</span>
                <p>
                  Un mismo cliente puede tener más de una gestión abierta. La clave funcional de trabajo es:
                </p>
              </div>
              <div className="import-rule__formula">
                <code>cod_cliente</code>
                <span>+</span>
                <code>num_gestion</code>
              </div>
              <p className="import-rule__update-note">
                Si la clave ya existe, se actualiza el registro sin duplicar; esto permite refrescar la deuda vencida.
              </p>
            </div>
          </div>

          <form onSubmit={handleUpload} className="import-upload">
            <div className="gv-form-group">
              <label className="gv-label">Archivo Excel (.xlsx) a importar</label>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="gv-input"
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              />
            </div>
            <div className="import-actions">
              <button
                type="button"
                className="gv-btn gv-btn--outline"
                disabled={isValidating || !file}
                onClick={handleValidateImport}
              >
                {isValidating ? 'Revisando...' : 'Revisar archivo'}
              </button>
              <button
                type="submit"
                className="gv-btn gv-btn--primary"
                disabled={isUploading || !validationResult || validationResult.validas === 0}
              >
                {isUploading ? 'Procesando...' : 'Importar validados'}
              </button>
            </div>
          </form>

          {uploadStatus && (
            <div
              className={`gv-alert ${uploadStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}
              style={{ marginTop: '16px' }}
            >
              {uploadStatus.msg}
            </div>
          )}

          {validationResult && (
            <div className="import-review">
              <div className="import-review__stats">
                <div className="import-stat">
                  <span>Total filas</span>
                  <strong>{validationResult.total_filas}</strong>
                </div>
                <div className="import-stat import-stat--ok">
                  <span>Correctas</span>
                  <strong>{validationResult.validas}</strong>
                </div>
                <div className="import-stat import-stat--warn">
                  <span>Observadas</span>
                  <strong>{validationResult.observadas}</strong>
                </div>
                <div className="import-stat">
                  <span>Nuevos</span>
                  <strong>{validationResult.nuevos}</strong>
                </div>
                <div className="import-stat">
                  <span>Actualizaciones</span>
                  <strong>{validationResult.actualizaciones}</strong>
                </div>
              </div>

              <div className="import-review__grid">
                <div className="import-result-panel">
                  <h4>Filas correctas</h4>
                  {validationResult.muestras_validas?.length > 0 ? (
                    <div className="import-result-list">
                      {validationResult.muestras_validas.map((fila) => (
                        <div key={`${fila.fila}-${fila.cod_cliente}-${fila.num_gestion}`} className="import-result-row ok">
                          <div>
                            <strong>Fila {fila.fila}: {fila.cod_cliente}</strong>
                            <span>{fila.r_social || 'Sin razón social'} · Gestión {fila.num_gestion}</span>
                          </div>
                          <em>{fila.accion === 'actualizar' ? 'actualiza sin duplicar' : 'nuevo'}</em>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="import-empty">No hay filas correctas en esta revisión.</p>
                  )}
                </div>

                <div className="import-result-panel">
                  <h4>Filas observadas</h4>
                  {validationResult.errores?.length > 0 ? (
                    <div className="import-result-list">
                      {validationResult.errores.map((fila) => (
                        <div key={`${fila.fila}-${fila.motivo}`} className="import-result-row warn">
                          <div>
                            <strong>Fila {fila.fila}: {fila.cod_cliente || 'sin cliente'}</strong>
                            <span>{fila.motivo}</span>
                          </div>
                          <em>omitida</em>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="import-empty">Sin observaciones. El archivo está limpio.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {activeTab === 'asignacion' && (
        <section className="gv-card gv-card--accent admin-card admin-card--wide">
          <div className="admin-card__heading">
            <div>
              <p className="gv-section-title">Asignación de técnicos</p>
              <h3>Casos importados para terreno</h3>
              <p>Asigna o reasigna cada cliente al técnico responsable. Los casos reprogramados vuelven a quedar disponibles para nueva visita.</p>
            </div>
            <button type="button" className="gv-btn gv-btn--outline" onClick={cargarClientesAdmin} disabled={clientesLoading}>
              {clientesLoading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          {clientesError && <div className="gv-alert gv-alert--error">{clientesError}</div>}

          <form className="bulk-assignment-panel" onSubmit={handleAsignarComuna}>
            <div>
              <p className="gv-section-title">Asignación masiva por comuna</p>
              <p>Selecciona una comuna y asigna todos sus casos pendientes al técnico responsable.</p>
            </div>
            <div className="bulk-assignment-grid">
              <div className="gv-form-group">
                <label className="gv-label">Comuna</label>
                <select className="gv-select" value={bulkComuna} onChange={(e) => setBulkComuna(e.target.value)} required>
                  <option value="">Selecciona comuna...</option>
                  {comunasAsignacion.map((comuna) => (
                    <option key={comuna} value={comuna}>{comuna}</option>
                  ))}
                </select>
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Técnico</label>
                <select className="gv-select" value={bulkTecnicoId} onChange={(e) => setBulkTecnicoId(e.target.value)} required>
                  <option value="">Selecciona técnico...</option>
                  {tecnicosAsignables.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nombre} - {roleLabel(usuario.rol)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="bulk-assignment-check">
                <input
                  type="checkbox"
                  checked={bulkSoloSinAsignar}
                  onChange={(e) => setBulkSoloSinAsignar(e.target.checked)}
                />
                Solo casos sin técnico válido
              </label>
              <button type="submit" className="gv-btn gv-btn--primary" disabled={bulkAssigning || clientesLoading}>
                {bulkAssigning ? 'Asignando...' : 'Asignar comuna'}
              </button>
            </div>
            {bulkComuna && (
              <div className="bulk-assignment-preview">
                <span>Vista previa</span>
                <strong>{bulkAsignacionPreview.total} caso(s) seran asignados</strong>
                <small>
                  {bulkAsignacionPreview.sinTecnico} sin tecnico - {bulkAsignacionPreview.asignados} ya asignado(s) en {bulkComuna}
                </small>
              </div>
            )}
            {bulkStatus && (
              <div className={`gv-alert ${bulkStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}>
                {bulkStatus.msg}
              </div>
            )}
          </form>

          <div className="assignment-toolbar">
            <div className="gv-form-group">
              <label className="gv-label">Buscar caso</label>
              <input
                type="search"
                className="gv-input"
                value={clienteFiltro}
                onChange={(e) => setClienteFiltro(e.target.value)}
                placeholder="Cliente, gestión, razón social, comuna o técnico"
              />
            </div>
            <div className="gv-form-group">
              <label className="gv-label">Estado de asignación</label>
              <select className="gv-select" value={filtroAsignacion} onChange={(e) => setFiltroAsignacion(e.target.value)}>
                <option value="pendientes">Pendientes de cierre</option>
                <option value="revision_admin">Decision admin</option>
                <option value="reprogramados">Reprogramados</option>
                <option value="sin_asignar">Sin técnico válido</option>
                <option value="asignados">Asignados a técnico</option>
                <option value="revisar_rol">Revisar rol</option>
                <option value="todos">Todos</option>
              </select>
            </div>
            <div className="gv-form-group">
              <label className="gv-label">Comuna</label>
              <select className="gv-select" value={filtroComunaAsignacion} onChange={(e) => setFiltroComunaAsignacion(e.target.value)}>
                <option value="">Todas las comunas</option>
                {comunasAsignacion.map((comuna) => (
                  <option key={comuna} value={comuna}>{comuna}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="assignment-quick-filters">
            <button
              type="button"
              className={filtroAsignacion === 'todos' ? 'active' : ''}
              onClick={() => setFiltroAsignacion('todos')}
            >
              <span>Total casos</span>
              <strong>{clientesAdmin.length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'sin_asignar' ? 'active warn' : 'warn'}
              onClick={() => setFiltroAsignacion('sin_asignar')}
            >
              <span>Sin tecnico valido</span>
              <strong>{clientesAdmin.filter((cliente) => !tieneTecnicoValido(cliente)).length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'asignados' ? 'active ok' : 'ok'}
              onClick={() => setFiltroAsignacion('asignados')}
            >
              <span>Asignados</span>
              <strong>{clientesAdmin.filter((cliente) => tieneTecnicoValido(cliente)).length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'reprogramados' ? 'active' : ''}
              onClick={() => setFiltroAsignacion('reprogramados')}
            >
              <span>Reprogramados</span>
              <strong>{clientesAdmin.filter((cliente) => cliente.estado === 'reprogramado').length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'revision_admin' ? 'active warn' : 'warn'}
              onClick={() => setFiltroAsignacion('revision_admin')}
            >
              <span>Decision admin</span>
              <strong>{clientesAdmin.filter((cliente) => tieneDecisionAdminPendiente(cliente)).length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'pendientes' ? 'active warn' : 'warn'}
              onClick={() => setFiltroAsignacion('pendientes')}
            >
              <span>Pendientes cierre</span>
              <strong>{clientesAdmin.filter((cliente) => cliente.estado !== 'finalizado').length}</strong>
            </button>
            <button
              type="button"
              className={filtroAsignacion === 'revisar_rol' ? 'active danger' : 'danger'}
              onClick={() => setFiltroAsignacion('revisar_rol')}
            >
              <span>Revisar rol</span>
              <strong>{clientesAdmin.filter((cliente) => tieneAsignacionNoTecnica(cliente)).length}</strong>
            </button>
          </div>

          <div className="assignment-summary assignment-summary--legacy">
            <div><span>Total casos</span><strong>{clientesAdmin.length}</strong></div>
            <div><span>Sin técnico válido</span><strong>{clientesAdmin.filter((cliente) => !tieneTecnicoValido(cliente)).length}</strong></div>
            <div><span>Asignados a técnico</span><strong>{clientesAdmin.filter((cliente) => tieneTecnicoValido(cliente)).length}</strong></div>
            <div><span>Decision admin</span><strong>{clientesAdmin.filter((cliente) => tieneDecisionAdminPendiente(cliente)).length}</strong></div>
            <div><span>Reprogramados</span><strong>{clientesAdmin.filter((cliente) => cliente.estado === 'reprogramado').length}</strong></div>
            <div><span>Revisar rol</span><strong>{clientesAdmin.filter((cliente) => tieneAsignacionNoTecnica(cliente)).length}</strong></div>
          </div>

          {clientesLoading && <p className="import-empty">Cargando casos importados...</p>}

          {!clientesLoading && clientesAsignacionFiltrados.length === 0 && (
            <p className="import-empty">No hay casos con esos filtros.</p>
          )}

          {clientesAdmin.length > 0 && (
            <div className="assignment-list-context">
              <div>
                <span>Vista actual</span>
                <strong>{clientesAsignacionFiltrados.length} de {clientesAdmin.length} caso(s)</strong>
              </div>
              <p>
                Filtro: {
                  filtroAsignacion === 'todos' ? 'Todos' :
                  filtroAsignacion === 'sin_asignar' ? 'Sin tecnico valido' :
                  filtroAsignacion === 'asignados' ? 'Asignados' :
                  filtroAsignacion === 'revision_admin' ? 'Decision admin' :
                  filtroAsignacion === 'reprogramados' ? 'Reprogramados' :
                  filtroAsignacion === 'revisar_rol' ? 'Revisar rol' :
                  'Pendientes de cierre'
                }
                {filtroComunaAsignacion ? ` - Comuna: ${filtroComunaAsignacion}` : ''}
              </p>
            </div>
          )}

          <div className="assignment-list">
            {clientesAsignacionFiltrados.map((cliente) => {
              const rowKey = `${cliente.cod_cliente}-${cliente.num_gestion}`;
              const tecnicoValido = tieneTecnicoValido(cliente);
              const asignacionNoTecnica = tieneAsignacionNoTecnica(cliente);
              const esReprogramado = cliente.estado === 'reprogramado';
              const esDecisionAdmin = tieneDecisionAdminPendiente(cliente);
              const esPrioridadAdmin = Number(cliente.prioridad_admin || 0) > 0;
              return (
                <article
                  key={cliente.id}
                  className={`assignment-row${!tecnicoValido ? ' assignment-row--needs-tech' : ''}${asignacionNoTecnica ? ' assignment-row--review-role' : ''}${(esReprogramado || esDecisionAdmin) ? ' assignment-row--reschedule' : ''}${esPrioridadAdmin ? ' assignment-row--priority' : ''}`}
                >
                  <div className="assignment-row__main">
                    <span>{cliente.cod_cliente} · Gestión {cliente.num_gestion}</span>
                    <strong>{cliente.r_social}</strong>
                    <small>{cliente.direccion}{cliente.comuna ? `, ${cliente.comuna}` : ''}</small>
                    {esPrioridadAdmin && <em className="assignment-row__tag assignment-row__tag--priority">Prioridad admin</em>}
                    {esDecisionAdmin && <em className="assignment-row__tag">Decision admin: {cliente.ultima_visita?.resultado_label || cliente.ultima_visita?.resultado_visita}</em>}
                    {esReprogramado && <em className="assignment-row__tag">Reprogramado - requiere nueva visita</em>}
                    {esDecisionAdmin && cliente.ultima_visita?.motivo_no_cierre && (
                      <small>{cliente.ultima_visita.motivo_no_cierre}</small>
                    )}
                  </div>
                  <div className="assignment-row__status">
                    <span className={`user-state ${tecnicoValido ? 'active' : 'blocked'}`}>
                      {tecnicoValido ? cliente.tecnico?.nombre : 'Requiere técnico'}
                    </span>
                    <em>{asignacionNoTecnica ? 'asignado a usuario no técnico' : clienteEstadoLabel(cliente.estado)}</em>
                  </div>
                  <div className="assignment-row__actions">
                    <div className="assignment-row__priority">
                      <span>Prioridad operativa</span>
                      <button
                        type="button"
                        className={`gv-btn ${esPrioridadAdmin ? 'gv-btn--amber' : 'gv-btn--outline'}`}
                        disabled={priorityKey === rowKey}
                        onClick={() => handleCambiarPrioridadCliente(cliente, !esPrioridadAdmin)}
                      >
                        {esPrioridadAdmin ? 'Quitar prioridad' : 'Marcar prioridad'}
                      </button>
                    </div>
                    <div className={`assignment-row__current-tech${tecnicoValido ? '' : ' assignment-row__current-tech--empty'}`}>
                      <span>Técnico actual</span>
                      <strong>{tecnicoValido ? cliente.tecnico?.nombre : 'Sin técnico válido'}</strong>
                      <small>
                        {tecnicoValido
                          ? `${roleLabel(cliente.tecnico?.rol) || 'Técnico de terreno'} · Responsable vigente`
                          : 'Selecciona un responsable para continuar'}
                      </small>
                    </div>
                    <div className="assignment-row__assign-copy">
                      <span>{!tecnicoValido ? 'Asignar técnico' : esReprogramado ? 'Nuevo técnico para la visita' : 'Nuevo técnico'}</span>
                      <small>{esDecisionAdmin ? 'Termina administrativamente o asigna una nueva visita' : esReprogramado ? 'Caso reprogramado: selecciona quién realizará la nueva visita' : tecnicoValido ? 'Selecciona solo si necesitas cambiar al responsable' : 'Asigna el primer responsable del caso'}</small>
                    </div>
                    {esDecisionAdmin && (
                      <button
                        type="button"
                        className="gv-btn gv-btn--amber"
                        disabled={closingKey === rowKey}
                        onClick={() => handleTerminarClienteAdmin(cliente)}
                      >
                        {closingKey === rowKey ? 'Terminando...' : 'Terminar'}
                      </button>
                    )}
                    <select
                      className="gv-select assignment-row__select"
                      value=""
                      aria-label={`${tecnicoValido ? 'Cambiar' : 'Asignar'} técnico para ${cliente.r_social || cliente.cod_cliente}. ${tecnicoValido ? `Técnico actual: ${cliente.tecnico?.nombre}.` : 'Sin técnico actual.'}`}
                      disabled={assigningKey === rowKey || tecnicosAsignables.length === 0}
                      onChange={(e) => handleAsignarTecnico(cliente, e.target.value)}
                    >
                      <option value="">{!tecnicoValido ? 'Selecciona técnico...' : esReprogramado ? 'Selecciona nuevo técnico para la visita...' : 'Selecciona nuevo técnico...'}</option>
                      {tecnicosAsignables
                        .filter((usuario) => String(usuario.id) !== String(cliente.tecnico_asignado || ''))
                        .map((usuario) => (
                          <option key={usuario.id} value={usuario.id}>
                            {usuario.nombre} - {roleLabel(usuario.rol)}
                          </option>
                        ))}
                    </select>
                    <select
                      className="gv-select"
                      value=""
                      disabled={assigningKey === rowKey || tecnicosAsignables.length === 0}
                      onChange={(e) => handleAsignarTecnico(cliente, e.target.value)}
                    >
                      <option value="">{tecnicoValido ? 'Reasignar técnico...' : 'Asignar técnico...'}</option>
                      {tecnicosAsignables.map((usuario) => (
                        <option key={usuario.id} value={usuario.id}>
                          {usuario.nombre} - {roleLabel(usuario.rol)}
                        </option>
                      ))}
                    </select>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {activeTab === 'respaldos' && (
        <section className="gv-card gv-card--accent admin-card admin-card--wide">
          <div className="admin-card__heading">
            <div>
              <p className="gv-section-title">Control de visitas realizadas</p>
              <h3>Respaldos y levantamiento en terreno</h3>
              <p>Consulta lo registrado por los técnicos, revisa evidencias y descarga el PDF formal de respaldo.</p>
              <small className="auto-refresh-note">Se sincroniza automaticamente cada 15 segundos entre administradores.</small>
            </div>
            <button type="button" className="gv-btn gv-btn--outline" onClick={() => cargarVisitasAdmin()} disabled={visitasLoading}>
              {visitasLoading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          {visitasError && <div className="gv-alert gv-alert--error">{visitasError}</div>}
          {visitasLoading && <p className="import-empty">Cargando visitas generadas...</p>}

          <form className="bulk-assignment-panel" onSubmit={handleDescargarPdfZip}>
            <div>
              <p className="gv-section-title">Descarga masiva</p>
              <p>Selecciona un mes o un rango de fechas y descarga todos los PDFs disponibles en un archivo ZIP.</p>
            </div>
            <div className="bulk-assignment-grid">
              <div className="gv-form-group">
                <label className="gv-label">Mes</label>
                <select
                  className="gv-select"
                  value={pdfZipMes}
                  onChange={(e) => aplicarMesZip(e.target.value)}
                >
                  <option value="">Rango manual</option>
                  {mesesRespaldos.map((mes) => (
                    <option key={mes.value} value={mes.value}>{mes.label}</option>
                  ))}
                </select>
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Desde</label>
                <input
                  type="date"
                  className="gv-input"
                  value={pdfZipDesde}
                  onChange={(e) => {
                    setPdfZipMes('');
                    setPdfZipDesde(e.target.value);
                  }}
                />
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Hasta</label>
                <input
                  type="date"
                  className="gv-input"
                  value={pdfZipHasta}
                  onChange={(e) => {
                    setPdfZipMes('');
                    setPdfZipHasta(e.target.value);
                  }}
                />
              </div>
              <button type="submit" className="gv-btn gv-btn--primary" disabled={pdfZipLoading}>
                {pdfZipLoading ? 'Generando ZIP...' : 'Descargar ZIP'}
              </button>
            </div>
            {pdfZipStatus && (
              <div className={`gv-alert ${pdfZipStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}>
                {pdfZipStatus.msg}
              </div>
            )}
          </form>

          {!visitasLoading && visitasAdmin.length === 0 && (
            <p className="import-empty">Aún no hay visitas registradas con respaldo PDF.</p>
          )}

          {visitasAdmin.length > 0 && (
            <>
            <div className="backup-inbox">
              {[
                { id: '', label: 'Todos', value: resumenRespaldos.total, hint: 'Respaldos registrados' },
                { id: 'pendientes', label: 'Pendientes', value: resumenRespaldos.pendientes, hint: 'PDF sin revisar' },
                { id: 'nuevos', label: 'Nuevos', value: resumenRespaldos.nuevos, hint: 'Sin descarga ni lectura' },
                { id: 'descargados', label: 'Descargados', value: resumenRespaldos.descargados, hint: 'PDF bajados' },
                { id: 'vistos', label: 'Leidos', value: resumenRespaldos.leidos, hint: 'Marcados como revisados' },
                { id: 'sin_pdf', label: 'Sin PDF', value: resumenRespaldos.sinPdf, hint: 'Cierre sin respaldo' },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`backup-inbox__item${filtroRevisionRespaldos === item.id ? ' active' : ''}`}
                  onClick={() => setFiltroRevisionRespaldos(item.id)}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                  <small>{item.hint}</small>
                </button>
              ))}
            </div>

            <div className="assignment-toolbar backup-toolbar">
              <div className="gv-form-group">
                <label className="gv-label">Mes de visita</label>
                <select
                  className="gv-select"
                  value={filtroMesRespaldos}
                  onChange={(e) => setFiltroMesRespaldos(e.target.value)}
                >
                  <option value="">Todos los meses</option>
                  {mesesRespaldos.map((mes) => (
                    <option key={mes.value} value={mes.value}>{mes.label}</option>
                  ))}
                </select>
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Filtrar por comuna</label>
                <select
                  className="gv-select"
                  value={filtroComunaRespaldos}
                  onChange={(e) => setFiltroComunaRespaldos(e.target.value)}
                >
                  <option value="">Todas las comunas</option>
                  {comunasRespaldos.map((comuna) => (
                    <option key={comuna} value={comuna}>{comuna}</option>
                  ))}
                </select>
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Estado revisión</label>
                <select
                  className="gv-select"
                  value={filtroRevisionRespaldos}
                  onChange={(e) => setFiltroRevisionRespaldos(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="pendientes">Pendientes de revision</option>
                  <option value="nuevos">Nuevos sin revisar</option>
                  <option value="vistos">Marcados como leídos</option>
                  <option value="descargados">Descargados</option>
                  <option value="no_descargados">No descargados</option>
                  <option value="sin_pdf">Sin PDF</option>
                </select>
              </div>
              <div className="gv-form-group assignment-toolbar__search">
                <label className="gv-label">Buscar respaldo</label>
                <input
                  type="search"
                  className="gv-input"
                  placeholder="Cliente, gestión, técnico, actividad..."
                  value={busquedaRespaldos}
                  onChange={(e) => setBusquedaRespaldos(e.target.value)}
                />
              </div>
              <div className="backup-list-summary">
                <span>Mostrando</span>
                <strong>{visitasRespaldosFiltradas.length}</strong>
                <small>de {visitasAdmin.length} respaldo(s)</small>
              </div>
            </div>
            </>
          )}

          {visitasAdmin.length > 0 && visitasRespaldosFiltradas.length === 0 && (
            <p className="import-empty">No hay respaldos PDF para los filtros seleccionados.</p>
          )}

          <div className="visit-admin-list">
            {visitasRespaldosFiltradas.map((visita) => (
              <article key={visita.id} className="visit-admin-card">
                <div className="visit-admin-card__main">
                  <div>
                    <span className="visit-admin-card__eyebrow">
                      {visita.cliente?.cod_cliente} · Gestión {visita.cliente?.num_gestion}
                    </span>
                    <h4>{visita.cliente?.r_social || 'Cliente sin razón social'}</h4>
                    <p>{visita.cliente?.direccion} {visita.cliente?.comuna ? `, ${visita.cliente.comuna}` : ''}</p>
                  </div>
                </div>

                <div className="visit-admin-card__status">
                  <span className={`user-state ${visita.pdf_disponible ? 'active' : 'blocked'}`}>
                    {visita.pdf_disponible ? 'PDF listo' : 'Sin PDF'}
                  </span>
                  {visita.pdf_descargado_at && (
                    <span className="user-state active">Descargado</span>
                  )}
                  {visita.pdf_visto_at ? (
                    <span className="user-state protected">Leído</span>
                  ) : (
                    <span className="user-state blocked">No leído</span>
                  )}
                </div>

                <div className="visit-admin-card__grid">
                  <div>
                    <span>Técnico</span>
                    <strong>{visita.tecnico?.nombre || 'No informado'}</strong>
                  </div>
                  <div>
                    <span>Resultado</span>
                    <strong>{visita.resultado_label || visita.resultado_visita || 'Finalizada'}</strong>
                  </div>
                  <div>
                    <span>Actividad</span>
                    <strong>{visita.tipo_actividad || 'No informada'}</strong>
                  </div>
                  <div>
                    <span>Atendido por</span>
                    <strong>{visita.nom_client || 'No informado'}</strong>
                  </div>
                  <div>
                    <span>Fecha visita</span>
                    <strong>{formatearFechaHora(visita.fech_termino || visita.fecha_ini)}</strong>
                  </div>
                  <div>
                    <span>Fotos</span>
                    <strong>{visita.fotos?.length || 0}</strong>
                  </div>
                  <div>
                    <span>Descargado</span>
                    <strong>{visita.pdf_descargado_at ? formatearFechaHora(visita.pdf_descargado_at) : 'No'}</strong>
                  </div>
                  <div>
                    <span>Leído por admin</span>
                    <strong>{visita.pdf_visto_at ? formatearFechaHora(visita.pdf_visto_at) : 'Pendiente'}</strong>
                  </div>
                </div>

                <div className="visit-admin-card__notes">
                  <span>Observaciones</span>
                  <p>{visita.motivo_no_cierre || visita.obs_visita || 'Sin observaciones registradas.'}</p>
                </div>

                <div className="visit-admin-card__actions">
                  <button
                    type="button"
                    className="gv-btn gv-btn--primary"
                    disabled={!visita.pdf_disponible}
                    onClick={() => handleDescargarPdf(visita)}
                  >
                    Descargar PDF
                  </button>
                  <button
                    type="button"
                    className="gv-btn gv-btn--outline"
                    disabled={!visita.pdf_disponible}
                    onClick={() => handleMarcarVistoPdf(visita, !visita.pdf_visto_at)}
                  >
                    {visita.pdf_visto_at ? 'Marcar no leído' : 'Marcar leído'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'usuarios' && (
        <>
        <section className="user-management-summary" aria-label="Resumen de usuarios">
          <div><span>Usuarios</span><strong>{usuarios.length}</strong><small>Registrados</small></div>
          <div><span>Activos</span><strong>{usuarios.filter((usuario) => usuario.activo).length}</strong><small>Con acceso habilitado</small></div>
          <div><span>Técnicos</span><strong>{usuarios.filter((usuario) => hasRole(usuario.rol, 'tecnico')).length}</strong><small>Disponibles para asignar</small></div>
          <div className={usuarios.some((usuario) => !usuario.totp_enabled) ? 'attention' : ''}>
            <span>2FA pendiente</span>
            <strong>{usuarios.filter((usuario) => !usuario.totp_enabled).length}</strong>
            <small>Requieren configuración</small>
          </div>
        </section>
        <div className="admin-layout">
          <section className="gv-card gv-card--accent admin-card">
            <p className="gv-section-title">Crear usuario</p>
            <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="gv-form-group">
                <label className="gv-label" htmlFor="new-user-name">Nombre completo</label>
                <input id="new-user-name" type="text" name="nombre" className="gv-input" placeholder="Nombre y apellido" required />
              </div>
              <div className="gv-form-group">
                <label className="gv-label" htmlFor="new-user-email">Correo electrónico</label>
                <input id="new-user-email" type="email" name="email" className="gv-input" placeholder="correo@gasvalpo.cl" required />
              </div>
              <div className="gv-form-group">
                <label className="gv-label" htmlFor="new-user-role">Rol del usuario</label>
                <select id="new-user-role" name="rol" className="gv-select" required>
                  <option value="">Selecciona el rol...</option>
                  {availableRoles.map((rol) => (
                    <option key={rol} value={rol}>{roleLabel(rol)}</option>
                  ))}
                </select>
              </div>
              <div className="password-generation-note">
                <strong>Clave temporal segura</strong>
                <span>
                  El sistema genera automáticamente una contraseña aleatoria al crear el usuario.
                  El usuario deberá cambiarla en su primer ingreso.
                </span>
              </div>
              <button type="submit" className="gv-btn gv-btn--amber">
                Crear usuario y generar clave segura
              </button>
              {temporaryPassword && (
                <div className="temporary-password">
                  <span>Contraseña temporal generada para {temporaryPassword.email}</span>
                  <code>{temporaryPassword.temporary_password}</code>
                  <button type="button" className="gv-btn gv-btn--outline gv-btn--sm" onClick={handleCopyTemporaryPassword}>
                    Copiar clave temporal
                  </button>
                  <p>Entregar al usuario por un canal interno seguro. Se solicitará cambio al ingresar.</p>
                </div>
              )}
            </form>
          </section>

          <section className="gv-card admin-card">
            <p className="gv-section-title">Usuarios registrados</p>
            <div className="user-list">
              {usuarios.length === 0 && (
                <p className="import-empty">No hay usuarios cargados desde la base de datos.</p>
              )}
              {usuarios.map((usuario) => (
                <div key={usuario.id} className="user-row">
                  <div>
                    <strong>{usuario.nombre}</strong>
                    <span>{usuario.email}</span>
                  </div>
                  <div className="user-row__meta">
                    <span className="user-role">{roleLabel(usuario.rol)}</span>
                    {isSuperadmin(usuario.rol) && <span className="user-state protected">Protegido</span>}
                    <span className={`user-state ${usuario.activo ? 'active' : 'blocked'}`}>
                      {usuario.activo ? 'Activo' : 'Inactivo'}
                    </span>
                    <span className={`user-state ${usuario.totp_enabled ? 'active' : 'blocked'}`}>
                      {usuario.totp_enabled ? '2FA activo' : '2FA pendiente'}
                    </span>
                  </div>
                  <div className="user-row__actions">
                    <button
                      type="button"
                      className="gv-btn gv-btn--outline gv-btn--sm"
                      disabled={isSuperadmin(usuario.rol) || resetPasswordUserId === usuario.id}
                      onClick={() => handleResetPassword(usuario)}
                    >
                      {resetPasswordUserId === usuario.id ? 'Generando...' : 'Reiniciar contraseña'}
                    </button>
                    <button
                      type="button"
                      className="gv-btn gv-btn--outline gv-btn--sm"
                      disabled={isSuperadmin(usuario.rol) || resetMfaUserId === usuario.id}
                      onClick={() => handleResetMfaUser(usuario)}
                    >
                      {resetMfaUserId === usuario.id ? 'Reiniciando...' : 'Re-vincular 2FA'}
                    </button>
                    <button
                      type="button"
                      className="gv-btn gv-btn--ghost gv-btn--sm"
                      disabled={isSuperadmin(usuario.rol)}
                      onClick={() => handleToggleActive(usuario.id)}
                    >
                      {usuario.activo ? 'Bloquear' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      className="gv-btn gv-btn--danger gv-btn--sm"
                      disabled={isSuperadmin(usuario.rol) || usuario.id === currentUser?.id || deletingUserId === usuario.id}
                      onClick={() => handleDeleteUser(usuario)}
                    >
                      {deletingUserId === usuario.id ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
        </>
      )}

      {activeTab === 'configuracion' && canManageConfig && (
        <section className="gv-card gv-card--accent admin-card admin-card--wide">
          <p className="gv-section-title">Configuración de seguridad</p>
          <div className="settings-status-strip" aria-label="Estado de configuración">
            <div>
              <span>Actividades vigentes</span>
              <strong>{tiposActividad.filter((actividad) => actividad.activo).length}</strong>
            </div>
            <div>
              <span>Usuarios editables</span>
              <strong>{editableUsers.length}</strong>
            </div>
            <div>
              <span>Tu doble factor</span>
              <strong>{mfaEnabled ? 'Activo' : 'Pendiente'}</strong>
            </div>
          </div>
          <div className="admin-settings">
            <div className="setting-panel setting-panel--wide">
              <h3>Tipos de actividad</h3>
              <p>Administra las actividades disponibles para que el técnico seleccione al cerrar una visita.</p>
              <form onSubmit={handleCrearActividad} className="activity-admin-form">
                <div className="gv-form-group">
                  <label className="gv-label" htmlFor="new-activity-name">Nueva actividad</label>
                  <input
                    id="new-activity-name"
                    type="text"
                    className="gv-input"
                    value={actividadNueva}
                    onChange={(e) => setActividadNueva(e.target.value)}
                    placeholder="Ej: Revision especial de empalme"
                    required
                  />
                </div>
                <button type="submit" className="gv-btn gv-btn--primary">
                  Agregar actividad
                </button>
              </form>

              {actividadStatus && (
                <div className={`gv-alert ${actividadStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}>
                  {actividadStatus.msg}
                </div>
              )}

              <div className="activity-admin-list activity-admin-list--compact">
                {tiposActividad.map((actividad) => (
                  <div key={actividad.id} className="activity-admin-row">
                    <div>
                      <strong>{actividad.nombre}</strong>
                      <span className={`user-state ${actividad.activo ? 'active' : 'blocked'}`}>
                        {actividad.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <div className="activity-admin-row__actions">
                      <button type="button" className="gv-btn gv-btn--ghost gv-btn--sm" onClick={() => handleToggleActividad(actividad)}>
                        {actividad.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button type="button" className="gv-btn gv-btn--danger gv-btn--sm" onClick={() => handleEliminarActividad(actividad)}>
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="setting-panel">
              <h3>Asignación de roles</h3>
              <p>Los administradores pueden operar usuarios técnicos. El superadmin queda protegido y no aparece como editable.</p>
              <div className="setting-grid">
                <div className="gv-form-group">
                  <label className="gv-label" htmlFor="role-user-select">Usuario</label>
                  <select
                    id="role-user-select"
                    className="gv-select"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    <option value="">Selecciona un usuario...</option>
                    {editableUsers.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nombre} - {roleLabel(usuario.rol)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="gv-form-group">
                  <label className="gv-label" htmlFor="new-role-select">Nuevo rol</label>
                  <select
                    id="new-role-select"
                    className="gv-select"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={!selectedUser}
                  >
                    {availableRoles.map((rol) => (
                      <option key={rol} value={rol}>{roleLabel(rol)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="button" className="gv-btn gv-btn--primary" onClick={handleAssignRole}>
                Aplicar rol
              </button>
            </div>

            <div className="setting-panel">
              <h3>Doble factor authenticator</h3>
              <p>Agrega un segundo factor con Google Authenticator, Microsoft Authenticator o una app compatible.</p>
              <span className={`user-state ${mfaEnabled ? 'active' : 'blocked'}`}>
                {mfaEnabled ? 'Activo' : 'No configurado'}
              </span>
              {!mfaEnabled && !mfaSetup && (
                <button type="button" className="gv-btn gv-btn--primary" onClick={handleStartMfaSetup}>
                  Activar doble factor
                </button>
              )}
              {mfaSetup && (
                <form onSubmit={handleConfirmMfa} className="mfa-setup">
                  <TotpQrCode value={mfaSetup.otpauth_url} />
                  <div>
                    <strong>Clave manual</strong>
                    <code>{mfaSetup.secret}</code>
                    <p>Abre tu app authenticator, agrega una cuenta manual y usa esta clave.</p>
                  </div>
                  <div>
                    <strong>URL authenticator</strong>
                    <code>{mfaSetup.otpauth_url}</code>
                  </div>
                  <div className="gv-form-group">
                    <label className="gv-label">Código de 6 dígitos</label>
                    <input
                      className="gv-input"
                      inputMode="numeric"
                      maxLength="6"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      required
                    />
                  </div>
                  <button type="submit" className="gv-btn gv-btn--primary">
                    Confirmar y activar
                  </button>
                </form>
              )}
              {mfaStatus && (
                <div className={`gv-alert ${mfaStatus.ok ? 'gv-alert--success' : 'gv-alert--error'}`}>
                  {mfaStatus.msg}
                </div>
              )}
            </div>

            <div className="setting-panel">
              <h3>Reinicio de acceso</h3>
              <p>
                Genera una contraseña temporal para entregar al usuario por un canal interno seguro.
                El superadmin no se puede reiniciar desde este panel.
              </p>
              <button
                type="button"
                className="gv-btn gv-btn--outline"
                onClick={handleResetPassword}
                disabled={!selectedUser || resetLoading}
              >
                {resetLoading ? 'Generando...' : 'Generar contraseña temporal'}
              </button>
              {temporaryPassword && (
                <div className="temporary-password">
                  <span>Contraseña temporal generada para {temporaryPassword.email}</span>
                  <code>{temporaryPassword.temporary_password}</code>
                  <button type="button" className="gv-btn gv-btn--outline gv-btn--sm" onClick={handleCopyTemporaryPassword}>
                    Copiar clave temporal
                  </button>
                  <p>Entregar al usuario y solicitar cambio al ingresar.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default DashboardAdmin;
